/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * SQLite dialect-native resolver for the `sqlite` engine (PRD Slice
 * "SQLite Resolver — Core").
 *
 * Pipeline mirrors `mysqlResolver.ts` (pure-text after GRA-7 Slice A4 — no
 * tree-sitter parse in the resolver hot path; chosen template because
 * SQLite also carries columns inline on the bus snapshot — no per-table
 * column cache):
 *   1. Derive `CompletionContext` from source text (statement isolation +
 *      paren-frame state machine + dotted-base extractor + alias/CTE
 *      bindings).
 *   2. Bail when inside a string or comment.
 *   3. Build `CompletionCandidate[]` from:
 *        - alias-dot column expansion (when the dotted base resolves to a
 *          bound alias),
 *        - position-aware columns + tables + CTEs + keywords + built-ins.
 *   4. Rank via `rankCandidates`.
 *   5. Translate each `MonacoCompletionItem` to Monaco's runtime shape.
 *
 * SQLite specifics:
 *   - **Identifier quoting** uses double quotes (`"name"`) by default — the
 *     ANSI form. SQLite ALSO accepts `[name]` (MSSQL-style) and `` `name` ``
 *     (MySQL-style) for input, so the unquote helper recognises all three;
 *     output canonicalises on `"…"`.
 *   - **Single-namespace model**: no schema, no cross-database joins by
 *     default. Attached databases (`ATTACH DATABASE 'foo' AS aux`) qualify
 *     as `aux.table` — the bus snapshot today only carries the main
 *     database; multi-database resolution is deferred to the Engine Extras
 *     slice.
 *   - **System tables** (`sqlite_master`, `sqlite_schema`, `sqlite_sequence`,
 *     `sqlite_stat1`) are NOT in the user's schema bus snapshot today
 *     because the introspection query filters `name NOT LIKE 'sqlite_%'`.
 *     The resolver never sees them, so no tier-8 demotion is needed at
 *     this layer — they're surfaced as keyword-tier candidates via the
 *     curated keyword list (`sqlite_master` is reachable via the static
 *     PRAGMA path covered in Engine Extras).
 *
 * Pure module — no Monaco/React imports at module-level. The `monaco`
 * runtime instance flows through resolver input.
 */

import type {
  AliasBinding,
  ColumnMeta,
  CompletionCandidate,
  CteBinding,
  DatabaseEngineId,
  FkEdge,
  TableMeta,
} from '@/core/engines';
import { ensureSqliteSchema } from '@/features/connections/services/language-services/sqliteSchemaCache';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { deriveCompletionContext } from '@/features/intellisense/context/deriveContext';
import { applyGroupByScopeFilter } from '@/features/intellisense/context/groupByScope';
import { isAfterCompletedFromRelation } from '@/features/intellisense/context/isAfterCompletedFromRelation';
import { isAfterCompletedSelectProjection } from '@/features/intellisense/context/isAfterCompletedSelectProjection';
import { scanOptionsFor } from '@/features/intellisense/context/slotTextScan';
import { applyParenScopeFilter } from './parenScopeFilter';
import { applyIndexTargetFilter } from './indexTargetFilter';
import { emitIndexNameCandidates } from './indexNameSlot';
import { filterAlreadyAssignedSetColumns } from './filterAssignedSetColumns';
import { emitAggregateArgHints, emitAggregateTailHints } from './aggregateArgHints';
import { emitProjectionWildcard } from './projectionWildcard';
import { emitSavepointNameCandidates } from '../context/savepointSlot';
import { isAtRenameTargetSlot } from '../context/renameTargetSlot';
import { emitTriggerPseudoRelationNames } from '../resolver/pseudoTables';
import { isAfterCompletedListItem } from '@/features/intellisense/context/nextKeywordSlot';
import {
  classifyNextKeywordSlot,
  type NextKeywordSlotResult,
} from '@/features/intellisense/context/nextKeywordSlot';
import { getLegalNextKeywords, isRowLimitSlot } from '@/features/intellisense/context/legalNextKeywords';
import {
  emitCompoundKeywordRemainders,
  keywordLabelsAlreadyOffered,
} from './compoundKeywordRemainder';
import { buildRowLimitOperandCandidates } from './rowLimitOperand';
import { inferSubqueryColumns } from '@/features/intellisense/resolver/inferSubqueryColumns';
import { emitNamedWindowCandidates } from '@/features/intellisense/resolver/inferNamedWindows';
import { resolveCteColumns } from '@/features/intellisense/resolver/resolveCteColumns';
import { emitSelectListAliasCandidates } from '@/features/intellisense/resolver/selectListAliases';
import {
  generateFkJoinSnippet,
  makeApplicableFksFinder,
} from '@/features/intellisense/fkJoin/fkJoinSnippet';
import { makeFkEndpointMatcher } from '@/features/intellisense/fkJoin/fkEndpointMatch';
import {
  computeBufferReferencedTableKeys,
  computeFkLinkedTableKeys,
  fkLinkedTableKey,
  unionTableKeys,
} from '@/features/intellisense/fkJoin/fkLinkedTableKeys';
import { promoteCaretBindingRelation } from '@/features/intellisense/fkJoin/caretBindingPromotion';
import { rankCandidates, buildRankOptions } from '@/features/intellisense/ranking/rank';
import { emitAliasCandidate } from './aliasSuggestions';
import { enrichCandidatesWithDocs } from './completionDocs';
import { toMonacoItem } from './monacoItem';

import type {
  OrchestratorResolver,
  OrchestratorResolverInput,
  OrchestratorResolverResult,
} from '../orchestrator/types';

import { emitCreateObjectNamespaces } from './createObjectNamespaces';
import {
  doubleQuote,
  foldIdentifier,
  formatIdentifier,
  formatQualifiedTwoPart,
  needsDoubleQuote,
  unquoteIdentifier,
} from './sqliteIdentifiers';
import { getSqliteKeywordsForPosition, SQLITE_KEYWORD_SET } from './sqliteKeywords';
import { getSqliteBuiltinFunctions } from './sqliteBuiltinFunctions';
import { emitValueSlotCandidates } from '../valueSlot/emitValueSlotCandidates';
import { emitPredicateHeadCandidates } from '../predicateHead/emitPredicateHeadCandidates';
import { buildSqliteValueSlotAdapter, collectSqliteScalarRoutineDescriptors } from './sqliteValueSlotAdapter';
import {
  emitResolvedValueCandidates,
  legalValueKeywordsFor,
  type ResolvedValueTarget,
} from '../valueSlot/emitResolvedValueCandidates';
import { coarseTypeOfDataType } from '../valueSlot/types';
import { findInsertValuesTarget } from '../context/findInsertValuesTarget';
import { findInsertReturningTarget } from '../context/findInsertReturningTarget';
import { computeClauseContext } from '@/features/sql-grammar/document/editorContextClause';
import {
  detectPragmaPosition,
  findAttachedDatabases,
  findFtsContentLiteralSlot,
  findInsertOnConflictTarget,
  getSqliteCollationNames,
  getSqliteFlexibleTypes,
  getSqliteGeneratedStorageKeywords,
  getSqlitePragmaNames,
  getSqlitePragmaValues,
  getSqliteStrictTypes,
  getSqliteSystemCatalogValueDomain,
  getSqliteTableOptions,
  getSqliteVirtualTableModules,
  isAnalyzeArgumentPosition,
  isCollationPosition,
  isCreateVirtualTableModulePosition,
  isDataTypePosition,
  isGeneratedStoragePosition,
  isReindexArgumentPosition,
  isSqliteAttachPathLiteralSlot,
  isStrictTableTypePosition,
  isTableOptionsPosition,
  makeShadowTablePredicate,
  SQLITE_ATTACH_PATH_HINTS,
  type AttachedDatabase,
  type SqliteInsertTarget,
} from './sqliteExtras';
import {
  extractInBufferAddedColumns,
  findFtsContentTableAhead,
  findTriggerOnTableAhead,
  isInsideTableDdlStatement,
} from './ddlStatementScan';
import { emitTypeAwareOperators, emitTypeAwareExpressionOperators } from '../operators/emitJsonOperatorCandidates';
import { findPrecedingExpressionType } from '../operators/findPrecedingExpressionType';
import {
  emitJsonPathCandidates,
  findJsonPathStringPosition,
} from '../operators/emitJsonPathCandidates';
import { findPrecedingColumnOperand } from '../operators/findPrecedingColumnOperand';
import { emitEnumValueCandidates } from '../operators/emitEnumValueCandidates';
import { computeEnumValueEdit } from '../operators/enumValueInsertion';
import { extractJsonPathFromCursorSqlite } from '../operators/extractJsonPathFromCursor.sqlite';
import { findValueStringBodyStart } from '../context/comparisonScanHelpers';
import { detectComparisonValueContext } from '../context/detectComparisonValueContext';
import { findTypedInsertColumns } from '../context/findTypedInsertColumns';
import { boundTablesFirst, resolveColumnRefToTable, type RelationResolver } from '../context/resolveColumnRefToTable';
import {
  applyInBufferTableOps,
  findInBufferCreatedTables,
  findInBufferCreatedViews,
  scanInBufferTableOps,
  type InBufferRename,
  type InBufferTable,
  type InBufferView,
} from '../context/inBufferTables';
import { getPathHints } from '@/features/sql-resolver/json/pathHints';
import {
  getJsonPathHints,
  isJsonColumnDeclared,
  getLooseRelationships,
} from '@/features/sql-resolver/metaInfoHints/metaInfoHintProvider';
import { emitLooseRelJoinSnippets } from '@/features/intellisense/fkJoin/looseFkJoinSnippet';
import { applyJoinOnColumnRanking } from '@/features/intellisense/fkJoin/fkColumnRanking';
import {
  applyJsonFunctionArgPromotion,
  collectBufferJsonFunctionColumns,
  isAtJsonFunctionFirstArg,
} from '@/features/intellisense/fkJoin/jsonFunctionArgAffinity';
import { isAtInsertTargetSlot } from '@/features/intellisense/context/isAtInsertTargetSlot';
import {
  findTriggerHostTable,
  type TriggerHostTable,
} from '@/features/intellisense/resolver/triggerHostTable';
import type { LooseRelationship } from '@/types/electron';

const ENGINE: DatabaseEngineId = 'sqlite';

// F5.2-CB1.7 — position-aware keyword promotion. When the cursor lands at
// a sub-position where a specific continuation keyword is the most likely
// next token, promote those keywords to tier 1 so they outrank schema
// candidates and the rest of the curated keyword list (which stays at
// tier 7). The `.filter()` guard against `SQLITE_KEYWORD_SET` keeps these
// sets honest — entries that aren't in the curated emit list are silently
// dropped here too, so a typo or future trim of the keyword list cannot
// produce a promoted candidate that never actually emits.
//
// After `SELECT <projection-complete>` → FROM / set-op continuation.
const SQLITE_NEXT_AFTER_SELECT_PROJECTION: ReadonlySet<string> = new Set(
  ['FROM', 'UNION', 'EXCEPT', 'INTERSECT'].filter((k) =>
    SQLITE_KEYWORD_SET.has(k),
  ),
);

// After `FROM <relation-complete>` → WHERE / join continuation / shaping
// clauses. Narrowed to SQLite's curated keyword surface.
const SQLITE_NEXT_AFTER_FROM_RELATION: ReadonlySet<string> = new Set(
  [
    'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS',
    'ON', 'USING', 'AS', 'ORDER', 'GROUP', 'HAVING', 'UNION', 'EXCEPT',
    'INTERSECT', 'LIMIT', 'OFFSET',
  ].filter((k) => SQLITE_KEYWORD_SET.has(k)),
);

// F5.3 Wave-4 — direction + null-ordering keywords at ORDER BY position.
// SQLite supports `ASC` / `DESC` direction and `NULLS FIRST` / `NULLS LAST`
// null-ordering modifiers. Surface them as tier-1 keyword candidates so
// they don't get drowned by the table/column flood at the bare ORDER BY
// slot. NULLS FIRST / NULLS LAST are multi-token keywords; passing them as
// a single candidate is the existing convention for ON DELETE CASCADE etc.
const SQLITE_ORDER_BY_DIRECTION_KEYWORDS: ReadonlySet<string> = new Set([
  'ASC',
  'DESC',
  'NULLS FIRST',
  'NULLS LAST',
]);

// F5.3 Wave-4 — null-comparison operator keywords at WHERE position.
// `IS NULL` and `IS NOT NULL` are multi-token operators not in the
// curated single-keyword list, so they were never emitted. The single-
// token `LIKE`, `BETWEEN`, `IN` already surface via the keyword list;
// included here so the operator vocabulary lands together at tier 1.
const SQLITE_WHERE_NULL_OPERATOR_KEYWORDS: ReadonlySet<string> = new Set([
  'IS NULL',
  'IS NOT NULL',
]);

// F5.3 Wave-4 — timing keywords at `CREATE TRIGGER <name> |` position.
const SQLITE_TRIGGER_TIMING_KEYWORDS: ReadonlySet<string> = new Set([
  'BEFORE',
  'AFTER',
  'INSTEAD OF',
]);

// JSON1 functions whose first argument names the JSON document column —
// drives the JSON-arg affinity lift at `json_extract(je.▮` (parity with the
// oracle JSON_VALUE surface). Lower-cased; the affinity regex is case-folded.
const SQLITE_JSON_FN_NAMES: readonly string[] = [
  'json_extract',
  'json_each',
  'json_tree',
  'json_type',
  'json_array_length',
  'json_valid',
  'json_quote',
  'json_set',
  'json_insert',
  'json_replace',
  'json_remove',
  'json_patch',
  'json_group_array',
  'json_group_object',
];

/**
 * F5.3 Wave-4 — SQLite window-frame slot detectors. SQLite supports
 * `RANGE|ROWS|GROUPS BETWEEN <start> AND <end>`. Frame-start lower bound
 * cannot be `UNBOUNDED FOLLOWING`; frame-end upper bound cannot be
 * `UNBOUNDED PRECEDING` — keyword sets honor those directional constraints.
 */
const SQLITE_FRAME_START_RE =
  /\b(?:ROWS|RANGE|GROUPS)\s+BETWEEN\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const SQLITE_FRAME_START_KEYWORDS: readonly string[] = [
  'UNBOUNDED PRECEDING',
  'CURRENT ROW',
];

const SQLITE_FRAME_END_RE =
  /\b(?:ROWS|RANGE|GROUPS)\s+BETWEEN\s+(?:UNBOUNDED\s+(?:PRECEDING|FOLLOWING)|\d+\s+(?:PRECEDING|FOLLOWING)|CURRENT\s+ROW)\s+AND\s+(?:[A-Za-z_][\w$]*)?\s*$/i;

const SQLITE_FRAME_END_KEYWORDS: readonly string[] = [
  'UNBOUNDED FOLLOWING',
  'CURRENT ROW',
];

function isSqliteFrameStartPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return SQLITE_FRAME_START_RE.test(slice);
}

function isSqliteFrameEndPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  return SQLITE_FRAME_END_RE.test(slice);
}

export const sqliteResolver: OrchestratorResolver = {
  engineId: ENGINE,
  resolve: async (input) => resolveSqlite(input),
};

async function resolveSqlite(
  input: OrchestratorResolverInput,
): Promise<OrchestratorResolverResult> {
  const { model, position, monaco } = input;
  const text = model.getValue();
  const offset = model.getOffsetAt(position);

  // Script-mode `ATTACH DATABASE 'foo.db' AS foo` is recorded in scriptScope by
  // the orchestrator. Forward scriptScope so ctx.scriptScope is populated, then
  // merge scriptScope.attachedDatabases with the text-parsed attached databases
  // so both orchestrator-pre-parsed and inline ATTACH statements surface in FROM.
  const scriptScope = input.scriptScope ?? {};
  const ctx = deriveCompletionContext(text, offset, ENGINE, scriptScope);
  const rankOptions = buildRankOptions(text, ctx, offset, input);
  // F5.2-CB7 → plan19 T7 — the fine-grained DDL slot kinds (`ddl-index-column`,
  // `ddl-fk-target`, `ddl-trigger-event`, `ddl-on-conflict-target`, …) now
  // arrive on `ctx.positionKind` from the token slot seam (`classifyDdlSlot`
  // via `deriveCompletionContext`'s DDL-override gate). The string-walk
  // `classifyDdlPositionKind` override that used to run here is retired.

  const snap = input.connectionId
    ? getSchemaBusSnapshot(input.connectionId, input.database)
    : null;

  // JCS Slice C4 — JSON path completion fires even inside a single-quoted
  // string when the string is in a recognised JSON-path position (after `->`,
  // `->>`, `json_extract(col, `). Runs BEFORE the string-bail so the cursor
  // inside `doc -> '|` or `json_extract(doc, '|` still gets candidates.
  if (input.connectionId && input.database) {
    const pathHintsCandidates = collectSqliteJsonPathCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: input.database,
      snapshotTables: snap?.tables ?? [],
      aliasBindings: ctx.aliasBindings,
      resolveTable: sqliteTableResolver(snap?.tables ?? []),
    });
    if (pathHintsCandidates.length > 0) {
      const wordInfo = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: wordInfo.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: wordInfo.endColumn,
      };
      const ranked = rankCandidates(pathHintsCandidates, ctx, String(wordInfo.word ?? ''), rankOptions);
      return { suggestions: ranked.map((item) => toMonacoItem(item, monaco, range)) };
    }
  }

  // Phase 3 — enum-value completion. Fires (like JSON-path) BEFORE the
  // string-bail because the cursor sits inside an unclosed string literal at
  // `WHERE status = '|`. Empty cache → 0 candidates → fall through.
  if (input.connectionId && input.database) {
    const enumCandidates = collectSqliteEnumValueCandidates({
      text,
      offset,
      connectionId: input.connectionId,
      database: input.database,
      snapshotTables: snap?.tables ?? [],
      aliasBindings: ctx.aliasBindings,
      resolveTable: sqliteTableResolver(snap?.tables ?? []),
    });
    if (enumCandidates.length > 0) {
      const edit = computeEnumValueEdit(model, position, text, offset);
      const ranked = rankCandidates(enumCandidates, ctx, edit.word, rankOptions);
      return {
        suggestions: ranked.map((item) => toMonacoItem(item, monaco, edit.range)),
      };
    }
  }

  // System-catalog value domains (`sqlite_master.type = '▮'`). Same
  // before-the-string-bail placement and the same shape as the MetaInfo enum
  // block above, but the domain is engine-fixed rather than declared, so it
  // needs no connection/database and fires on a cold cache too.
  {
    const catalogValues = collectSqliteSystemCatalogValueCandidates({
      text,
      offset,
      aliasBindings: ctx.aliasBindings,
    });
    if (catalogValues.length > 0) {
      const edit = computeEnumValueEdit(model, position, text, offset);
      const ranked = rankCandidates(catalogValues, ctx, edit.word, rankOptions);
      return {
        suggestions: ranked.map((item) => toMonacoItem(item, monaco, edit.range)),
      };
    }
  }

  // I6 — FTS5 content= / content_rowid= literal slots. Inside the single-quoted
  // option values of `CREATE VIRTUAL TABLE … USING fts5(…)`, surface the content
  // (source) table names and that table's columns. Runs BEFORE the string-bail,
  // like the JSON-path / enum collectors, because the cursor is inside a literal.
  {
    const ftsCandidates = collectSqliteFtsContentCandidates({
      text,
      offset,
      statementStart: ctx.statementRange.start,
      snapshotTables: snap?.tables ?? [],
    });
    if (ftsCandidates.length > 0) {
      const edit = computeEnumValueEdit(model, position, text, offset);
      const ranked = rankCandidates(ftsCandidates, ctx, edit.word, rankOptions);
      return {
        suggestions: ranked.map((item) => toMonacoItem(item, monaco, edit.range)),
      };
    }
  }

  // `ATTACH DATABASE '▮' AS aux` — a path literal. A file path cannot be
  // enumerated, but SQLite's magic in-memory forms can, and they are not
  // discoverable any other way. Runs BEFORE the string bail like the
  // collectors above. Closes sqlite script.64 §1.
  if (isSqliteAttachPathLiteralSlot(text, offset, ctx.statementRange.start)) {
    const pathCandidates: CompletionCandidate[] = SQLITE_ATTACH_PATH_HINTS.map(
      (hint, index) => ({
        // Bare label: the caret is INSIDE the quotes the buffer already has.
        label: hint.label,
        insertText: hint.label,
        isSnippet: false,
        kind: 'snippet',
        tier: 1,
        detail: hint.detail,
        orderHint: index,
      }),
    );
    const edit = computeEnumValueEdit(model, position, text, offset);
    const ranked = rankCandidates(pathCandidates, ctx, edit.word, rankOptions);
    return { suggestions: ranked.map((item) => toMonacoItem(item, monaco, edit.range)) };
  }

  if (ctx.insideStringOrComment) return { suggestions: [] };

  const wordInfo = model.getWordUntilPosition(position);
  const typed = String(wordInfo.word ?? '');
  // Cold cache → warm fire-and-forget. Subsequent keystrokes will see the
  // populated snapshot. Same fire-and-forget contract MSSQL / PG / MySQL use.
  if (!snap && input.connectionId && input.database) {
    void ensureSqliteSchema({
      connectionId: input.connectionId,
      database: input.database,
    }).catch(() => undefined);
  }

  // PRAGMA position is a hard short-circuit — the entire suggestion list is
  // either pragma names or pragma values, nothing else. Detected from the
  // statement-start tokens.
  const pragmaPos = detectPragmaPosition(
    text,
    offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
  );

  const onConflictTarget = findInsertOnConflictTarget(
    text,
    ctx.statementRange.start,
    ctx.statementRange.end,
  );
  // F5.2-CB2 — when the cursor lies inside a CREATE TRIGGER body, `NEW.|` /
  // `OLD.|` resolve to the trigger target table's columns (NOT to any
  // inner DML target in the trigger body).
  const triggerHostTable = findTriggerHostTable(text, offset, 'sqlite');
  const strictTypePos = isStrictTableTypePosition(
    text,
    offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
  );
  const dataTypePos =
    !strictTypePos &&
    isDataTypePosition(text, offset, ctx.statementRange.start, ctx.statementRange.end);
  const collationPos = isCollationPosition(
    text,
    offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
  );
  const generatedStoragePos = isGeneratedStoragePosition(
    text,
    offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
  );
  const tableOptionsPos = isTableOptionsPosition(
    text,
    offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
  );
  const attachedFromText = findAttachedDatabases(text);
  // Merge: scriptScope.attachedDatabases (from orchestrator's pre-cursor scan)
  // takes precedence over text-parsed ones; deduplicate by alias so ATTACH
  // statements already in the text don't appear twice.
  // ScriptScope.attachedDatabases lacks the `definedAt` range (it's an
  // orchestrator-level abstraction); use a sentinel {start:0,end:0}.
  const scopeAttached: readonly AttachedDatabase[] = (scriptScope.attachedDatabases ?? []).map(
    (a) => ({ alias: a.alias, path: a.path, definedAt: { start: 0, end: 0 } } satisfies AttachedDatabase),
  );
  // Third source: databases ATTACHed to the LIVE connection, read from
  // `PRAGMA database_list` at snapshot refresh. This is the only one that can
  // answer for an attachment made in an EARLIER buffer (or by another tab) —
  // the coverage harness attaches in a setup block and the checkpoint buffer
  // carries no ATTACH statement of its own. Their tables ride in
  // `snap.tables` with `database: <alias>`, so `aux_db.▮` and an alias bound
  // to `aux_db.tbl` resolve through the ordinary relation paths.
  const snapshotAttached: readonly AttachedDatabase[] = (snap?.attachedDatabases ?? []).map(
    (a) => ({ alias: a.alias, path: a.path, definedAt: { start: 0, end: 0 } } satisfies AttachedDatabase),
  );
  const seenAliases = new Set<string>();
  const attachedDatabases: readonly AttachedDatabase[] = [
    ...scopeAttached,
    ...attachedFromText,
    ...snapshotAttached,
  ].filter((a) => {
    const folded = a.alias.toLowerCase();
    if (seenAliases.has(folded)) return false;
    seenAliases.add(folded);
    return true;
  });

  // Surface tables (incl. `CREATE VIRTUAL TABLE … USING rtree(…)` and tables
  // in an ATTACHed db) CREATEd earlier in the same buffer so later statements
  // see them and their columns before the schema-bus snapshot refreshes. The
  // normal table-emit + alias-resolution paths pick the merged entries up
  // transparently. Closes sqlite script 37 (rtree) / 38 (ATTACH) in-buffer
  // gaps. ATTACHed-db tables carry their db alias as the `schema` qualifier.
  // Apply in-buffer RENAME/DROP to the snapshot FIRST, then merge in-buffer
  // CREATEs on top — so a `DROP TABLE foo; CREATE TABLE foo;` re-create leaves
  // `foo` present (applying ops after the merge would drop the just-created one).
  const filteredTables = mergeInBufferTables(
    applyInBufferTableOps(
      filterShadowTables(snap?.tables ?? [], input.sqliteRevealShadowTables ?? false),
      scanInBufferTableOps(text, offset, ENGINE),
      {
        keyOf: (t) => `${(t.database ?? '').toLowerCase()}.${t.name.toLowerCase()}`,
        keyOfRef: (q, n) => `${(q ?? '').toLowerCase()}.${n.toLowerCase()}`,
        renameTable: (existing, op: InBufferRename) => ({
          name: op.toName,
          ...(op.toQualifier !== null ? { database: op.toQualifier } : {}),
          kind: existing?.kind ?? 'table',
          columns: existing?.columns ?? [],
        }),
      },
    ),
    findInBufferCreatedTables(text, offset, ENGINE),
  );

  // In-buffer relations whose columns are a PROJECTION rather than a declared
  // list — `CREATE VIEW v AS SELECT …` and `CREATE TABLE t AS SELECT …`
  // (CTAS). `findInBufferCreatedTables` above cannot see either (it requires a
  // `(` column list after the name), so a later `alias.▮` against the new
  // relation resolved to nothing. Closes sqlite script.49 §8/§9/§10.
  const tablesWithProjections = mergeInBufferProjectedRelations(
    filteredTables,
    findInBufferCreatedViews(text, offset, ENGINE),
    text,
    ctx.cteBindings,
  );

  const looseRels =
    input.connectionId && input.database
      ? getLooseRelationships(input.connectionId, input.database)
      : [];

  const candidates = await buildCandidates({
    ctx,
    text,
    snapshotTables: tablesWithProjections,
    snapshotForeignKeys: snap?.foreignKeys ?? [],
    looseRels,
    cteBindings: ctx.cteBindings,
    currentDatabase: input.database,
    connectionId: input.connectionId,
    pragmaPos,
    onConflictTarget,
    triggerHostTable,
    strictTypePos,
    dataTypePos,
    collationPos,
    generatedStoragePos,
    tableOptionsPos,
    attachedDatabases,
  });

  // `USING (▮)` / `ROLLUP(▮)` / Oracle's `SAMPLE (▮)` admit operands or
  // nothing — never the tables and FK-join snippets the full emission adds.
  const scoped = applyIndexTargetFilter(
    applyParenScopeFilter(candidates, text, offset, ctx.statementRange.start, ENGINE),
    text, offset, ctx.statementRange.start, ENGINE,
  );
  // GROUP BY scope (`context/groupByScope.ts`) — SQLite is the DEMOTE half of
  // the matrix: a bare non-grouped column in an aggregate query is accepted
  // and returns a value from an arbitrary row of the group, so the reference
  // stays offered and is only ranked below the grouped columns. Matches
  // sqlite script.57 §1's "raw Invoice columns … at a lower tier".
  const grouped = applyGroupByScopeFilter(scoped, {
    text,
    offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engineId: ENGINE,
    positionKind: ctx.positionKind,
  });
  const withDocs = enrichCandidatesWithDocs(grouped, {
    aliasBindings: ctx.aliasBindings,
    snapshotTables: tablesWithProjections,
    foreignKeys: snap?.foreignKeys ?? [],
    fold: foldIdentifier,
  });
  const ranked = rankCandidates(withDocs, ctx, typed, rankOptions);

  const range = {
    startLineNumber: position.lineNumber,
    startColumn: wordInfo.startColumn,
    endLineNumber: position.lineNumber,
    endColumn: wordInfo.endColumn,
  };
  const suggestions = ranked.map((item) => toMonacoItem(item, monaco, range));
  return { suggestions };
}

/**
 * Heuristic shadow-table filter. Drops FTS5 / r*tree shadow siblings unless
 * the user explicitly opted into reveal. The filter only fires when the
 * prefix table (without the suffix) is also present in the snapshot — that
 * sibling presence is the signal that the suffixed table is a shadow.
 */
function filterShadowTables(
  tables: ReadonlyArray<SnapshotTable>,
  reveal: boolean,
): ReadonlyArray<SnapshotTable> {
  if (reveal) return tables;
  const isShadow = makeShadowTablePredicate(tables.map((t) => t.name));
  return tables.filter((t) => !isShadow(t.name));
}

// Merge in-buffer CREATE TABLE entries with the schema-bus snapshot. Skips
// entries colliding (folded db.name) with a snapshot table. The generic
// `qualifier` maps to SQLite's `database` field (an ATTACHed-db alias) — the
// field `resolveRelation` / the FROM-emit path match on; unqualified tables
// live in the default `main` db and leave it unset.
function mergeInBufferTables(
  snapshot: ReadonlyArray<SnapshotTable>,
  inBuffer: ReadonlyArray<InBufferTable>,
): ReadonlyArray<SnapshotTable> {
  if (inBuffer.length === 0) return snapshot;
  const keyOf = (db: string | null | undefined, name: string): string =>
    `${(db ?? '').toLowerCase()}.${name.toLowerCase()}`;
  const known = new Set<string>();
  for (const t of snapshot) known.add(keyOf(t.database, t.name));
  const merged: SnapshotTable[] = [...snapshot];
  for (const t of inBuffer) {
    const db = t.qualifier ?? null;
    if (known.has(keyOf(db, t.name))) continue;
    merged.push({
      name: t.name,
      ...(db !== null ? { database: db } : {}),
      kind: t.isTemporary ? 'temporary-table' : 'table',
      columns: t.columns.map((c) => ({ name: c.name, dataType: c.dataType })),
    });
  }
  return merged;
}

/**
 * Merge in-buffer relations whose columns come from a SELECT PROJECTION —
 * `CREATE VIEW v AS SELECT …` and `CREATE TABLE t AS SELECT …` (CTAS). Both
 * are produced by `findInBufferCreatedViews`, which captures the body range;
 * the column names are inferred from it with the shared subquery-projection
 * helper (the same one that answers `alias.▮` for a derived table).
 *
 * Mirrors mysql's `mergeInBufferViews`. Kept per-resolver rather than shared
 * because the snapshot row shape and qualifier field differ per engine
 * (sqlite has `database` for ATTACHed dbs and no `schema`).
 */
function mergeInBufferProjectedRelations(
  tables: ReadonlyArray<SnapshotTable>,
  relations: ReadonlyArray<InBufferView>,
  text: string,
  cteBindings: readonly CteBinding[],
): ReadonlyArray<SnapshotTable> {
  if (relations.length === 0) return tables;
  const keyOf = (db: string | null | undefined, name: string): string =>
    `${(db ?? '').toLowerCase()}.${name.toLowerCase()}`;
  const known = new Set<string>();
  for (const t of tables) known.add(keyOf(t.database, t.name));
  const merged: SnapshotTable[] = [...tables];
  for (const r of relations) {
    const db = r.qualifier ?? null;
    if (known.has(keyOf(db, r.name))) continue;
    // `inferSubqueryColumns` expects a paren-wrapped body (its outermost
    // SELECT sits at paren-depth 1); a view / CTAS body is bare.
    const wrapped = `(${text.slice(r.bodyStart, r.bodyEnd)})`;
    const colNames =
      r.explicitColumns && r.explicitColumns.length > 0
        ? r.explicitColumns
        : inferSubqueryColumns({
            source: wrapped,
            bodyRange: { start: 0, end: wrapped.length },
            engineId: ENGINE,
            snapshotTables: merged,
            cteBindings,
          });
    if (colNames.length === 0) continue;
    known.add(keyOf(db, r.name));
    merged.push({
      name: r.name,
      ...(db !== null ? { database: db } : {}),
      kind: r.relationKind ?? 'view',
      columns: colNames.map((name) => ({ name, dataType: '' })),
    });
  }
  return merged;
}

type SnapshotTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: string;
  readonly columns: ReadonlyArray<{
    readonly name: string;
    readonly dataType: string;
    // Carried by the bus snapshot's ColumnMeta; read by the pkFirst mode of
    // the REFERENCES / constraint column-list emission.
    readonly isPrimaryKey?: boolean;
    // Single-column UNIQUE coverage (bus ColumnMeta.isUnique) — read by the
    // ON CONFLICT conflict-target filter.
    readonly isUnique?: boolean;
    readonly jsonClassification?: import('@/features/sql-resolver/types/schema').JsonClassification;
    // Carried by the bus snapshot's ColumnMeta (T18b, D13) — read to build
    // the NULL keyword legality of a typed INSERT VALUES element.
    readonly nullable?: boolean;
  }>;
  // Indexes on the relation — the index-NAME vocabulary of `DROP INDEX ▮`
  // (see `IndexMeta`; undefined on engines whose adapter does not source it).
  readonly indexes?: ReadonlyArray<{
    readonly name: string;
    readonly columns: readonly string[];
    readonly isUnique: boolean;
    readonly isPrimary: boolean;
  }>;
};

type BuildArgs = {
  readonly ctx: ReturnType<typeof deriveCompletionContext>;
  readonly text: string;
  readonly snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly snapshotForeignKeys: readonly FkEdge[];
  readonly looseRels: readonly LooseRelationship[];
  readonly cteBindings: readonly CteBinding[];
  readonly currentDatabase?: string;
  /** T18b — typed VALUES element enum-value lookup (MetaInfo is keyed per
   *  connection/database). Absent in some tests; the emitter degrades to no
   *  enum-value candidates when either this or `currentDatabase` is missing. */
  readonly connectionId?: string;
  readonly pragmaPos: ReturnType<typeof detectPragmaPosition>;
  readonly onConflictTarget: SqliteInsertTarget | null;
  readonly triggerHostTable: TriggerHostTable | null;
  readonly strictTypePos: boolean;
  readonly dataTypePos: boolean;
  readonly collationPos: boolean;
  readonly generatedStoragePos: boolean;
  readonly tableOptionsPos: boolean;
  readonly attachedDatabases: readonly AttachedDatabase[];
};

async function buildCandidates(args: BuildArgs): Promise<CompletionCandidate[]> {
  const {
    ctx,
    snapshotTables,
    snapshotForeignKeys,
    pragmaPos,
    strictTypePos,
    dataTypePos,
    collationPos,
    generatedStoragePos,
    tableOptionsPos,
  } = args;
  const out: CompletionCandidate[] = [];

  // Compound-keyword remainder — when the caret is attached to the second (or
  // third) word of a multi-word keyword (`CURRENT R▮`, `UNBOUNDED P▮`,
  // `NEXT V▮`, `REFRESH ON D▮`, `READS S▮`, `ON DUPLICATE K▮`), offer what is
  // left of that keyword. Runs FIRST so the keyword-only short-circuits below
  // (which return `out` as it stands) carry it; additive, so a caret whose head
  // word is really an identifier keeps everything else it had. See
  // `compoundKeywordRemainder.ts`.
  emitCompoundKeywordRemainders(
    out,
    ENGINE,
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.positionKind,
  );

  // PRAGMA hard short-circuit — the entire suggestion list is dialect-specific.
  if (pragmaPos) {
    if (pragmaPos.kind === 'name') {
      // script.63 §3 — when the name slot is followed by a call-style `(`
      // (`PRAGMA ▮table_info('Customer')`), the arg-taking introspection
      // pragmas are the likely intent: promote them to tier 1.
      emitPragmaNames(out, {
        promoteFunctionStyle: /^[\w]*\s*\(/.test(args.text.slice(ctx.offset)),
      });
    }
    else if (pragmaPos.kind === 'table-arg') emitTables(out, snapshotTables,
      ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
    );
    else emitPragmaValues(out, pragmaPos.pragmaName);
    return out;
  }

  // STRICT-mode column-type position — emit the 6 STRICT types at tier 2,
  // and also surface the flexible-mode names (`NUMERIC`, `VARCHAR`,
  // `BOOLEAN`, …) at tier 3 (deprioritised). STRICT mode rejects them at
  // runtime, but the user may be migrating between STRICT and non-STRICT
  // tables — being honest about what SQLite accepts as a token is better
  // than hiding them entirely.
  if (strictTypePos) {
    emitStrictTypes(out);
    emitFlexibleTypesDeprioritised(out);
    return out;
  }

  // Non-STRICT data-type slot (CREATE TABLE column-type, ALTER TABLE ADD
  // COLUMN type, CAST AS target). Emit the full type-name vocabulary at
  // tier 1 — STRICT types lead, flexible-mode names follow. We DON'T `return`
  // here: appending the curated keyword list at tier 7 keeps the user's
  // partial-typed-word completion behaviour (e.g. typing `NOT NULL` after a
  // type) responsive — but the type candidates outrank everything else.
  if (dataTypePos) {
    emitDataTypeCandidates(out);
    return out;
  }

  // After-COLLATE position (`… COLLATE |`, `REINDEX |`) — emit the three
  // collation names at tier 1. Hard short-circuit because nothing else is
  // legal at this slot in SQLite.
  //
  // `REINDEX ▮` is the one shape where the collation names are only half the
  // answer: the grammar is
  // `REINDEX [collation-name | [schema.](table-name | index-name)]`, so the
  // relation inventory belongs here too (script.42 §3). Collations stay at
  // tier 1 ahead of them — the collation form is the rarer, harder-to-recall
  // half, and a table name is one the user can also reach by typing it.
  if (collationPos) {
    emitCollationNames(out);
    if (isReindexArgumentPosition(
      args.text, ctx.offset, ctx.statementRange.start, ctx.statementRange.end,
    )) {
      emitTables(out, snapshotTables,
        ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
      );
    }
    return out;
  }

  // After a GENERATED expression's `)` — emit VIRTUAL and STORED at tier 1.
  // Hard short-circuit because these two keywords are the only meaningful
  // continuation in the storage-class slot.
  if (generatedStoragePos) {
    emitGeneratedStorageKeywords(out);
    return out;
  }

  // F5.3 Wave-4 — CREATE TABLE table-options slot. Cursor sits after the
  // closing `)` of the column list and before `;` — the only legal next
  // tokens are `WITHOUT ROWID`, `STRICT`, or the comma-combined form.
  // Hard short-circuit: emitting tables / functions here just drowns the
  // two relevant keywords. Closes sqlite Script 45 §6.
  if (tableOptionsPos) {
    for (const kw of getSqliteTableOptions()) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  const positionKind = ctx.positionKind;

  if (positionKind === 'dotted-access' && ctx.dottedBase) {
    emitDottedAccess(out, args);
    return out;
  }

  // `ROLLBACK TO ▮` / `RELEASE ▮` — a savepoint NAME slot. The name is
  // transaction state the buffer itself declared, so no schema emission can
  // reach it; before this the caret fell to `unknown` and answered with the
  // table list. Shared across all five engines (each spells the statements
  // differently — see `savepointSlot.ts`). Closes sqlite script.50 §9/§10,
  // and the `SAVEPOINT ▮` declaration slot (§1/§6) where the name is the
  // user's to invent and the honest answer is an empty list.
  if (emitSavepointNameCandidates(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    return out;
  }

  // Index-NAME slot. `DROP INDEX ▮` names an INDEX, not a relation, but
  // the caret falls to 'unknown' and answered with the table list. Shared
  // recognizer + emitter (`indexNameSlot.ts`); it claims the caret only when
  // the snapshot actually carries index metadata for the engine, so an engine
  // whose adapter has not populated `TableMeta.indexes` yet is unchanged.
  if (emitIndexNameCandidates(out, {
    text: args.text,
    offset: ctx.offset,
    statementStart: ctx.statementRange.start,
    statementEnd: ctx.statementRange.end,
    engine: ENGINE,
    tables: args.snapshotTables,
    format: formatIdentifier,
  })) {
    return out;
  }

  // `ALTER TABLE t RENAME TO ▮` — the NEW name. Every name the schema could
  // offer is a name the rename would collide with, so the slot is closed.
  // Shared with pgsql / mysql / oracle (see `renameTargetSlot.ts`). Closes
  // sqlite script.35 §6.
  if (isAtRenameTargetSlot(args.text, ctx.offset, ctx.statementRange.start, ENGINE)) {
    return out;
  }

  // Value-slot plan T12 — scalar-value branch (sqlite clone of the T8 pgsql
  // exemplar). Reached ONLY when the engine's capability table (or the T5b
  // test setter) admits the slot's role; LIVE in production as of the T13e
  // flip (`valueSlotCapabilities.ts`). The shared policy owns the candidate
  // set; sqlite built-in functions stay engine-emitted at their tier,
  // mirroring the arm's own emission below.
  if (positionKind === 'scalar-value' && ctx.valueSlot) {
    const { adapter, scope } = buildSqliteValueSlotAdapter({
      slot: ctx.valueSlot,
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotForeignKeys,
      // C2 P1-1/P2-3 — virtual-relation fallback: json_each/json_tree fixed
      // TVF columns and the sqlite_master-family system tables, via the
      // same maps `emitAliasColumns` consults.
      virtualColumnsFor: (binding) => {
        const tvfCols = SQLITE_JSON_TVF_COLUMNS.get(binding.relation.toLowerCase());
        if (tvfCols) return tvfCols.map((name) => ({ name }));
        const relationFolded = foldIdentifier(binding.relation);
        return SQLITE_SYSTEM_TABLE_COLUMNS[relationFolded]?.map((name) => ({ name }));
      },
    });
    await emitValueSlotCandidates(out, ctx.valueSlot, adapter, scope);
    emitBuiltinFunctions(out);
    // T13e no-worse parity (F5.3-R, clone of pgsqlResolver's T13a /
    // mssqlResolver's T13b / mysqlResolver's T13c / oracleResolver's T13d
    // fix) — the clause path surfaces SELECT-list projection aliases at
    // HAVING positions. A comparison-RHS slot that overrode the HAVING
    // clause must keep them. Recompute the clause with the same machine
    // `deriveCompletionContext` used (the slot path drops it); WHERE/JOIN-ON
    // slots never gain aliases — projections aren't computed yet at those
    // points in SQL evaluation order.
    const scalarClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, ENGINE,
    )?.clause;
    if (scalarClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, ENGINE, 'having',
        (name) => formatIdentifier(name),
      );
    }
    return out;
  }

  // Predicate-head plan P6d/P8d — predicate-head branch (sqlite clone of the
  // P5/P7 pgsql exemplar), LIVE in production since the P8d flip (sqlite
  // carries all three head roles in `predicateHeadCapabilities.ts`). Reached
  // when the capability gate admits the head's role. Shape-mirrors the
  // scalar-value branch above: shared policy owns the candidate set, sqlite
  // built-in functions stay engine-emitted at their existing tier (the
  // PH-D4 builtin exemption — they carry no return metadata to tier
  // against, exactly as the scalar branch ships them).
  if (positionKind === 'predicate-head' && ctx.predicateHead) {
    const { adapter } = buildSqliteValueSlotAdapter({
      // No `slot`: PH-D3's no-LHS mode. The overload makes this a distinct
      // signature, so a scalar-value caller cannot reach it by dropping its
      // slot.
      text: args.text,
      aliasBindings: ctx.aliasBindings,
      cteBindings: ctx.cteBindings,
      snapshotTables,
      snapshotForeignKeys,
      // C2 P1-1/P2-3 — same virtual-relation fallback as the scalar branch:
      // json_each/json_tree fixed TVF columns and sqlite_master-family
      // system tables.
      virtualColumnsFor: (binding) => {
        const tvfCols = SQLITE_JSON_TVF_COLUMNS.get(binding.relation.toLowerCase());
        if (tvfCols) return tvfCols.map((name) => ({ name }));
        const relationFolded = foldIdentifier(binding.relation);
        return SQLITE_SYSTEM_TABLE_COLUMNS[relationFolded]?.map((name) => ({ name }));
      },
    });
    await emitPredicateHeadCandidates(out, { head: ctx.predicateHead }, adapter);
    emitBuiltinFunctions(out);
    // Post-policy parity (P1-sol F8) — the same engine-specific follow-up the
    // scalar-value branch replays: the clause path surfaces SELECT-list
    // projection aliases at HAVING positions, and a head slot that overrode
    // the HAVING clause must keep them. WHERE/JOIN-ON heads never gain
    // aliases — projections aren't computed yet at those points in SQL
    // evaluation order.
    const headClause = computeClauseContext(
      args.text, ctx.statementRange.start, ctx.offset, ENGINE,
    )?.clause;
    if (headClause === 'having') {
      emitSelectListAliasCandidates(
        out, args.text, ctx.statementRange, ctx.offset, ENGINE, 'having',
        (name) => formatIdentifier(name),
      );
    }
    return out;
  }

  // §4.5 — named-window completion after `OVER `/`OVER (`. Additive (tier 1,
  // no short-circuit): the WINDOW-clause names rank above the position's
  // generic keyword/column vocabulary, which the pipeline still emits below.
  // Reads `QueryStructure.windows` for the current statement (decision 7).
  if (!ctx.insideStringOrComment) {
    emitNamedWindowCandidates({
      out,
      text: args.text,
      offset: ctx.offset,
      statementStart: ctx.statementRange.start,
      statementEnd: ctx.statementRange.end,
      engine: 'sqlite',
    });
  }

  // F5.2-CB7 — DDL slot dispatch. Each branch is purposely narrow: the slot's
  // candidate set is a strict subset of the general completion universe so
  // unrelated suggestions don't dilute the user's choices.
  if (positionKind === 'ddl-index-column') {
    const target = findIndexTargetTable(args.text, ctx.statementRange.start, ctx.offset);
    if (target) emitTargetTableColumns(out, target, snapshotTables);
    // SQLite indexes may be built on an EXPRESSION, not just a bare column
    // (`CREATE INDEX ix ON Track(LOWER(Name))`), so the built-in functions are
    // part of this slot's vocabulary — at their usual tier 8, below the target
    // table's columns. Closes sqlite script.31 §6.
    emitBuiltinFunctions(out);
    return out;
  }
  if (positionKind === 'ddl-create-object-name') {
    // `CREATE [TEMP] TABLE|VIEW|INDEX|TRIGGER ▮` — the NEW object's name. Only
    // a schema qualifier can precede it. ENGINE DIVERGENCE (documented at the
    // site per the parity rule): SQLite has no schema catalog — its namespaces
    // are the two built-in databases plus whatever the session ATTACHed — so
    // the vocabulary is `main` / `temp` / attached aliases instead of a
    // snapshot schema list. `main` is where a bare-named object is created, so
    // it leads the tier. No object-type exception applies here (unlike MSSQL /
    // PG / MySQL): SQLite accepts `schema.index_name` and
    // `schema.trigger_name` too — see `createObjectNamespaces.ts`.
    emitCreateObjectNamespaces(out, ['main', 'temp'], {
      defaultName: 'main',
      kind: 'database',
      detail: 'Built-in schema',
      format: formatIdentifier,
    });
    emitAttachedDatabases(out, args.attachedDatabases);
    return out;
  }
  if (positionKind === 'ddl-fk-target') {
    // FK target slot — only table names (no keywords, no built-ins) so the
    // user picks a referenced table from the schema. A FK can only reference a
    // base table, never a view — exclude views.
    const fkTargetTables = snapshotTables.filter(
      (t) => t.kind !== 'view' && t.kind !== 'materialized-view',
    );
    emitTables(out, fkTargetTables,
      ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, fkTargetTables)
          : new Set<string>(),
    );
    return out;
  }
  if (positionKind === 'ddl-trigger-event') {
    // Only legal continuation keywords after BEFORE/AFTER/INSTEAD OF in a
    // CREATE TRIGGER are the three DML verbs. Promoted to tier 1.
    emitTriggerEventKeywords(out);
    return out;
  }
  if (positionKind === 'ddl-on-conflict-target') {
    // The conflict-target column list (script.40 §2) only accepts columns a
    // unique index can arbitrate on — PK + single-column UNIQUE. Other
    // columns are a runtime error ("ON CONFLICT clause does not match any
    // PRIMARY KEY or UNIQUE constraint"), so they are filtered out, not
    // just deprioritised. The DO UPDATE SET / WHERE surfaces (handled in
    // the fallback branch) keep the full column set.
    if (args.onConflictTarget) {
      emitOnConflictTargetColumns(out, args.onConflictTarget, snapshotTables, {
        uniqueOnly: true,
      });
    }
    return out;
  }
  if (positionKind === 'ddl-references-column-list') {
    // Cursor inside `REFERENCES <tbl>(|` — emit the REFERENCED table's
    // columns (the FK target's column list). Reuses the index-target-table
    // emission helper after extracting the referenced table from the FK
    // segment.
    const target = findReferencesTargetTable(args.text, ctx.statementRange.start, ctx.offset);
    if (target) {
      emitTargetTableColumns(out, target, snapshotTables, { pkFirst: true });
    }
    return out;
  }
  if (positionKind === 'ddl-create-trigger-timing') {
    // Cursor at `CREATE [OR REPLACE] [TEMP] TRIGGER <name> |` — emit only
    // the timing-keyword vocabulary; nothing else makes sense here.
    for (const kw of SQLITE_TRIGGER_TIMING_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }
  // F5.3 Wave-4 — SQLite window-frame slot dispatch. Independent of
  // positionKind. Closes goals/autocomplete-coverage sqlite script.55 §3/§4.
  if (isSqliteFrameStartPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of SQLITE_FRAME_START_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }
  if (isSqliteFrameEndPosition(args.text, ctx.offset, ctx.statementRange.start)) {
    for (const kw of SQLITE_FRAME_END_KEYWORDS) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: 1,
        detail: 'SQL keyword',
      });
    }
    return out;
  }

  if (positionKind === 'ddl-alter-table-constraint-column-list') {
    // GRA8-ROLL-5 — reused as the `ALTER TABLE <tbl> RENAME COLUMN ▮` existing-
    // column slot (the slot seam routes RENAME COLUMN here; `classifyDdlPosition
    // Kind` returns null for it). Emit the live columns of the ALTER target
    // table so the user picks the column to rename — tables must NOT bleed in.
    const target = findSqliteAlterTableTarget(
      args.text,
      ctx.statementRange.start,
      ctx.offset,
    );
    if (target) {
      emitTargetTableColumns(out, target, snapshotTables);
      // The snapshot is built from the LIVE database, so a column an earlier
      // statement in this very buffer added with `ALTER TABLE … ADD COLUMN`
      // is missing from it — and at a RENAME COLUMN caret that is often the
      // column the user means. Closes sqlite script.35 §4.
      emitInProgressColumns(
        out,
        extractInBufferAddedColumns(args.text, ctx.offset, {
          schema: null,
          name: target,
          fold: foldIdentifier,
        }),
      );
    }
    return out;
  }

  if (positionKind === 'ddl-target-table-column') {
    // A column list whose owning table is named AFTER the caret, so it is
    // bound by looking ahead. Two shapes reach here:
    //   `CREATE TRIGGER … BEFORE UPDATE OF ▮ ON <tbl>` (script.33 §2) — the
    //   restriction list, table from the `ON` clause;
    //   `CREATE VIRTUAL TABLE … USING fts5(▮ … content='<tbl>')`
    //   (script.34 §2/§3) — an external-content FTS table mirrors the source
    //   table's columns, so those are the names to offer.
    // They are mutually exclusive by construction (a virtual-table statement
    // has no `ON` clause), so trying the trigger form first is unambiguous.
    // An fts5 table with NO `content=` option is self-contained: there is
    // nothing to mirror, and emitting nothing beats flooding with relations.
    const trigger = findTriggerOnTableAhead(args.text, ctx.offset, ctx.statementRange.end);
    const contentTable =
      trigger?.name
      ?? findFtsContentTableAhead(args.text, ctx.offset, ctx.statementRange.end);
    if (contentTable) {
      emitTargetTableColumns(out, foldIdentifier(contentTable), snapshotTables);
    }
    return out;
  }

  if (positionKind === 'ddl-create-table-expression') {
    // Cursor inside a CHECK / GENERATED / PRIMARY KEY / UNIQUE /
    // FOREIGN KEY column-list inside a CREATE TABLE — the new table
    // doesn't exist in the schema yet, so emit the columns declared
    // SO FAR (parsed from the CREATE TABLE preamble). When the slot is
    // a `GENERATED ALWAYS AS (...)` expression, drop the column being
    // currently defined — referencing self in the expression is invalid.
    const inProgressCols = extractCreateTableInProgressColumns(
      args.text,
      ctx.statementRange.start,
      ctx.offset,
    );
    const dropCurrent = isInsideGeneratedExpression(args.text, ctx.offset);
    const emitted = dropCurrent && inProgressCols.length > 0
      ? inProgressCols.slice(0, -1)
      : inProgressCols;
    emitInProgressColumns(out, emitted);
    // A CHECK body is a PREDICATE, not a bare column list: the user needs the
    // comparison and boolean operators to finish it (`CHECK (Quantity > 0)`).
    // The PK / UNIQUE / FK column lists take a comma-separated list of plain
    // names, where an operator would be a syntax error — so the vocabulary is
    // gated on the CHECK lead. Closes sqlite script.29 §5 + script.65 §1.
    if (isInsideCheckPredicate(args.text, ctx.statementRange.start, ctx.offset)) {
      emitSqliteCheckPredicateOperators(out);
    }
    return out;
  }

  // I3 parity — INSERT target slot (`INSERT [OR REPLACE|IGNORE] INTO ▮`). The
  // grammar lumps this under `values`/`unknown`, so the bound-alias columns of
  // the INSERT's SELECT body would otherwise bury the relation names. Fire only
  // once INTO has been typed (`!needsInto`) — the bare `INSERT ▮` /
  // `INSERT ▮OR REPLACE` tail is ambiguous with the INSERT modifier slot.
  const insertSlot = isAtInsertTargetSlot(args.text, ctx.offset, ctx.statementRange.start);
  if (insertSlot && !insertSlot.needsInto) {
    emitTables(out, snapshotTables,
      ctx.cteBindings.length === 0
        ? computeBufferReferencedTableKeys(args.text, snapshotTables)
        : new Set<string>(),
    );
    emitBuiltinSchemas(out);
    emitAttachedDatabases(out, args.attachedDatabases);
    return out;
  }

  if (positionKind === 'from') {
    // Sub-position: cursor after a completed relation / alias (mirrors the
    // MSSQL resolver). Another bare relation here would be a syntax error;
    // emit only continuation keywords via the shared legal-next-keyword model.
    const fromSlot = classifyNextKeywordSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
      positionKind,
      ctx.aliasBindings,
      ENGINE,
    );
    if (
      fromSlot &&
      (fromSlot.slot === 'after-from-relation-plain' || fromSlot.slot === 'after-join-target')
    ) {
      // Generated table-alias candidate leads the list (orderHint 0); the
      // continuation keywords shift to orderHint 1+. See aliasSuggestions.ts.
      const aliasEmitted = emitAliasCandidate(out, {
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        slot: fromSlot,
        aliasBindings: ctx.aliasBindings,
        keywords: SQLITE_KEYWORD_SET,
      });
      emitLegalNextKeywords(out, fromSlot, aliasEmitted ? 1 : 0);
      return out;
    }
    // Any OTHER slot the classifier names at a `from` position is a boundary
    // where a relation is illegal — a set operator starting a new query branch
    // (`… FROM t UNION ▮`), a shape keyword. Emit its keywords and stop, rather
    // than falling through to the table list below.
    //
    // A slot with an EMPTY list (the word is a construct in some other dialect,
    // e.g. a table legally named `tablesample` in MySQL) counts as DECLINED —
    // it must reach the after-relation keyword fallback below, not the bare
    // relation list, which is a syntax error at a completed relation.
    const fromSlotEmitted = !!fromSlot && emitLegalNextKeywords(out, fromSlot);
    if (fromSlotEmitted) return out;
    // Null-fallback: when the classifier declines an after-relation slot it
    // can't categorize confidently but the broader text-walk still says we're
    // past a completed relation, preserve the pre-classify behaviour — emit the
    // curated continuation keywords rather than offering illegal bare tables.
    if (!fromSlotEmitted && isAfterCompletedFromRelation(args.text, ctx.offset, ctx.statementRange.start)) {
      emitKeywords(out, SQLITE_NEXT_AFTER_FROM_RELATION);
      return out;
    }
    emitTables(
      out,
      snapshotTables,
      unionTableKeys(
        computeFkLinkedTableKeys(ctx.aliasBindings, snapshotTables, snapshotForeignKeys, sqliteBindingResolver(snapshotTables)),
        ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
      ),
    );
    // S6 — the relation already written at the caret's own binding outranks
    // the other tier-1 (FK-linked / buffer-referenced) tables.
    promoteCaretBindingRelation(out, ctx.aliasBindings, ctx.offset);
    emitSqliteSystemTables(out);
    emitBuiltinSchemas(out);
    emitAttachedDatabases(out, args.attachedDatabases);
    emitCteCandidates(out, ctx);
    if (ctx.aliasBindings.length > 0 && snapshotForeignKeys.length > 0) {
      emitFkJoinSnippets(out, ctx.aliasBindings, snapshotTables, snapshotForeignKeys);
    }
    if (ctx.aliasBindings.length > 0 && args.looseRels.length > 0) {
      emitLooseRelJoinSnippets({
        out,
        bindings: ctx.aliasBindings,
        tables: snapshotTables,
        looseRels: args.looseRels,
        realFks: snapshotForeignKeys,
        engine: ENGINE,
        resolveBinding: sqliteBindingResolver(snapshotTables),
      });
    }
    return out;
  }

  if (
    positionKind === 'join-on' ||
    positionKind === 'where' ||
    positionKind === 'having' ||
    positionKind === 'set' ||
    positionKind === 'values' ||
    positionKind === 'returning' ||
    positionKind === 'group-by' ||
    positionKind === 'order-by' ||
    positionKind === 'select-list' ||
    positionKind === 'subquery' ||
    positionKind === 'cte-def' ||
    positionKind === 'limit-offset' ||
    positionKind === 'unknown'
  ) {
    // T18b — typed VALUES element (Docs/AutocompleteValueSlot-Plan.md). When
    // the cursor sits inside a VALUES element whose target column is
    // positively known (explicit column list; findInsertValuesTarget), the
    // resolved-target policy (T18a) replaces the generic values-position
    // emission below and short-circuits — columns/relations are structurally
    // illegal here. NO reclassification (G23): the position stays
    // positionKind 'values'; this is resolver-side dispatch only.
    if (positionKind === 'values') {
      const hit = emitTypedValuesElement(out, args, ctx);
      if (hit) return out;
    }
    // T19 — INSERT RETURNING list (D11: INSERT-context only). Emit ONLY the
    // target table's columns; the relation/keyword flood never serves this
    // position. UPDATE/DELETE RETURNING falls through unchanged.
    if (positionKind === 'returning') {
      const hit = emitInsertReturningCandidates(out, args, ctx);
      if (hit) return out;
    }
    // Position-aware continuation-keyword boundaries (`WHERE x = 1 |`,
    // `GROUP BY x |`, `ORDER BY x |`, `… ON a=b |`, `SELECT * |`). When the
    // classifier confidently names one of these keyword-only sub-positions,
    // emit ONLY the legal ordered keywords and short-circuit so columns /
    // tables / generic keywords don't dilute the boundary. The FROM-relation
    // and join-target slots are handled by the `from` arm above, so exclude
    // them here. A `null` classification (e.g. `WHERE |`, `SELECT a, |`) falls
    // through to today's full emission unchanged.
    const kwSlot = classifyNextKeywordSlot(
      args.text,
      ctx.offset,
      ctx.statementRange.start,
      positionKind,
      ctx.aliasBindings,
      ENGINE,
    );
    // Anywhere inside the row-limiting clause (`LIMIT ▮` … `FETCH FIRST 10 ROWS
    // ONLY ▮`) only integers, the clause's own keywords and in-scope integer
    // operands are legal — relations, columns and the general keyword list are
    // a syntax error at every one of those carets. Emit that and stop; the
    // schema emission below must not run.
    if (kwSlot && isRowLimitSlot(kwSlot.slot)) {
      return buildRowLimitOperandCandidates(out, kwSlot.slot, ENGINE);
    }
    if (
      kwSlot &&
      kwSlot.slot !== 'after-from-relation-plain' &&
      kwSlot.slot !== 'after-join-target'
    ) {
      if (emitLegalNextKeywords(out, kwSlot)) return out;
    }
    // F5.3 R-bucket — `ANALYZE <here>` accepts a schema name (main / temp /
    // attached DB) OR a table name (qualified or bare). The position
    // classifier currently treats it as 'unknown' so user tables already
    // surface via `emitTables` below; we additionally emit the database-name
    // set so the spec's database-form lands. Closes script.42 §2.
    if (isAnalyzeArgumentPosition(args.text, ctx.offset, ctx.statementRange.start)) {
      emitBuiltinSchemas(out);
      emitAttachedDatabases(out, args.attachedDatabases);
    }
    // F5.3 R-bucket — `CREATE VIRTUAL TABLE <name> USING <here>` module slot.
    // Closes script.34 §1.
    if (isCreateVirtualTableModulePosition(args.text, ctx.offset, ctx.statementRange.start)) {
      emitVirtualTableModules(out);
    }
    if (ctx.aliasBindings.length > 0) {
      emitAllBoundAliasColumns(out, ctx.aliasBindings, snapshotTables, ctx.cteBindings, args.text);
      // The trigger row qualifiers themselves (`NEW` / `OLD`). Inside a
      // trigger body there is no unqualified row scope, so one of these is the
      // first token the user must type to reach a column — yet only their
      // COLUMNS were ever emitted, never the qualifier.
      emitTriggerPseudoRelationNames(
        out, args.text, ctx.statementRange, ctx.aliasBindings, ENGINE, ctx.offset,
      );
    }
    if (args.onConflictTarget) {
      emitOnConflictTargetColumns(out, args.onConflictTarget, snapshotTables);
    }
    // T16: the S0 value-slot relation guard (plan: Docs/AutocompleteValueSlot-Plan.md
    // T3/T4) that used to gate the two emitters below on
    // `!detectComparisonValueContext(...)` is RETIRED — every comparison-value
    // operand in where/having/join-on now classifies via the LIVE scalar-value
    // branch above (comparison-rhs, set-rhs, between-low, between-high), which
    // returns before this expression arm is ever reached.
    //
    // P8d (Docs/AutocompletePredicateHead-Plan.md): bare predicate HEADS
    // (`WHERE ▮`, `HAVING ▮`, `ON ▮`, `AND/OR/NOT ▮` continuations) no longer
    // reach this arm either — they classify 'predicate-head' and return from
    // the LIVE head branch above, where relations are FORBIDDEN_KINDS
    // (`WHERE <table>` is illegal SQL). What still lands here under
    // where/having/join-on is the recognizer's conservative refusals —
    // mid-expression boundaries (`WHERE x ▮`), `WHERE (▮` open-paren heads,
    // CASE WHEN heads, IS-chain territory (PH-D6 v1 gaps) — plus the
    // non-predicate positionKinds of this arm (select-list/unknown/…). For
    // those the full relation emission below is unchanged by design (the
    // no-worse invariant; the v1 gaps are recorded in the P10 gaps sweep).
    emitTables(out, snapshotTables,
      ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
    );
    emitCteCandidates(out, ctx);
    emitBuiltinFunctions(out);
    // F5.3-R-bucket: surface SELECT-list projection aliases in HAVING /
    // ORDER-BY / GROUP-BY positions. SQLite accepts an unqualified `AS`-bound
    // alias in all three clauses.
    emitSelectListAliasCandidates(
      out,
      args.text,
      ctx.statementRange,
      ctx.offset,
      'sqlite',
      positionKind,
      (name) => formatIdentifier(name),
    );
    // F5.2-CB1.7 — when the cursor sits AFTER a completed projection in the
    // SELECT list (`SELECT * `, `SELECT col1, col2 `), promote FROM / set-op
    // continuation keywords to tier 1. Other positions (where, join-on,
    // having, group-by, order-by, set, values, returning, cte-def, …) fall
    // through with the default empty set → keywords stay at tier 7.
    const nextLikely =
      positionKind === 'select-list' &&
      isAfterCompletedSelectProjection(
            args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE),
          )
        ? SQLITE_NEXT_AFTER_SELECT_PROJECTION
        : (new Set<string>() as ReadonlySet<string>);
    emitProjectionWildcard(out, positionKind, ENGINE);
    emitKeywords(out, nextLikely);
    // F5.3 Wave-4 — direction + null-ordering keywords at ORDER BY position.
    // After a completed ORDER BY expression (`c.FirstName <here>`), SQLite
    // accepts `ASC`, `DESC`, `NULLS FIRST`, `NULLS LAST`. The multi-token
    // NULLS FIRST / NULLS LAST aren't in the curated single-keyword list, so
    // emit them directly as tier-1 candidates.
    if (positionKind === 'order-by') {
      // After a completed sort key these are the likely next token. At the
      // HEAD of the clause a sort key is still owed, so they stay available —
      // the user may be editing an existing item — but must not outrank the
      // columns the position actually calls for.
      const afterSortKey = isAfterCompletedListItem(
        args.text, ctx.offset, ctx.statementRange.start, scanOptionsFor(ENGINE),
      );
      for (const kw of SQLITE_ORDER_BY_DIRECTION_KEYWORDS) {
        out.push({
          label: kw,
          insertText: kw,
          isSnippet: false,
          kind: 'keyword',
          tier: afterSortKey ? 1 : 7,
          detail: 'SQL keyword',
        });
      }
    }
    // F5.3 Wave-4 — multi-token null-comparison operators at WHERE position.
    // `IS NULL` / `IS NOT NULL` aren't in the curated single-keyword list, so
    // emit them directly. Closes sqlite Script 31 §4 + Script 43 §4.
    if (positionKind === 'where' || positionKind === 'having') {
      for (const kw of SQLITE_WHERE_NULL_OPERATOR_KEYWORDS) {
        out.push({
          label: kw,
          insertText: kw,
          isSnippet: false,
          kind: 'keyword',
          tier: 1,
          detail: 'SQL keyword',
        });
      }
    }
    // JCS Slice C3: type-aware SQLite JSON operator emission for `JSON`
    // columns governed by `CHECK (json_valid(col))` or MetaInfo (once bus
    // surfaces declarations). Surfaces `->`, `->>`, `json_extract`,
    // `json_valid`, `EXISTS json_each(col)`.
    emitAggregateArgHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitAggregateTailHints(out, args.text, ctx.offset, ctx.statementRange.start, ENGINE);
    emitTypeAwareJsonOperators(out, args);
    // F5.2-A — at INSERT column-list slots (`INSERT INTO t (a, |)`) drop
    // already-typed column names so the next slot doesn't re-offer them.
    // Closes goals/autocomplete-coverage sqlite script.7 §2 + script.16 §2
    // (and the same pattern across other engines via a shared helper).
    // A column may be assigned only once in an UPDATE, so the SET list's
    // already-assigned names are illegal at the next slot.
    filterAlreadyAssignedSetColumns(
      out, args.text, ctx.offset, ctx.statementRange.start, ctx.statementRange.end, ENGINE,
    );
    if (positionKind === 'values') {
      filterAlreadyTypedInsertColumns(out, args, ctx);
    }
    return out;
  }

  emitTables(out, snapshotTables,
    ctx.cteBindings.length === 0
          ? computeBufferReferencedTableKeys(args.text, snapshotTables)
          : new Set<string>(),
  );
  emitKeywords(out);
  return out;
}

/**
 * T18b — when the cursor sits inside a VALUES element whose target column is
 * positively known (`findInsertValuesTarget`), resolve the target column in
 * the snapshot (`resolveRelation` — the same database-scoped lookup the
 * enum/JSON-path phase-3 collectors use) and emit the shared resolved-target
 * policy (T18a) — enum values (tier 1), NULL/DEFAULT keywords (D13), a
 * scalar-subquery snippet, and compat-tiered scalar routines (sqlite carries
 * none — the snippet/keyword pieces still apply). Returns `false` (caller
 * falls through to the unchanged generic emission) when the element isn't
 * positively typed or the target table/column can't be resolved.
 */
function emitTypedValuesElement(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): boolean {
  // Trigger bodies put a row scope in play (`NEW.`/`OLD.`), so columns are
  // NOT structurally illegal at VALUES elements there — the resolved-target
  // premise (`legalSources.columns: false`) fails and the generic emission
  // must serve the position. Pinned by coverage script.36 §5/§6.
  if (args.triggerHostTable) return false;
  const hit = findInsertValuesTarget(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!hit) return false;
  const tbl = resolveRelation(
    args.snapshotTables,
    foldIdentifier(hit.table.name),
    hit.table.schema ? foldIdentifier(hit.table.schema) : null,
  );
  if (!tbl) return false;
  const colFolded = foldIdentifier(hit.columnName);
  const column = tbl.columns.find((c) => foldIdentifier(c.name) === colFolded);
  if (!column) return false;

  const columnMeta: ColumnMeta = {
    name: column.name,
    dataType: column.dataType,
    nullable: column.nullable ?? false,
    isPrimaryKey: column.isPrimaryKey ?? false,
    ordinal: 0,
  };

  const connectionId = args.connectionId;
  const database = args.currentDatabase;
  const target: ResolvedValueTarget = {
    kind: 'insert-column',
    column: columnMeta,
    coarseType: coarseTypeOfDataType(column.dataType),
    legalSources: { columns: false, keywords: legalValueKeywordsFor(ENGINE, columnMeta) },
    collectEnumValues:
      connectionId && database
        ? () => {
            const tmp: CompletionCandidate[] = [];
            emitEnumValueCandidates(tmp, {
              connectionId,
              database,
              // SQLite snapshot generator doesn't populate `tbl.schema` (sqlite
              // has one natural schema, "main") — mirrors
              // `collectSqliteEnumValueCandidates`'s own default.
              schema: tbl.schema || 'main',
              table: tbl.name,
              column: column.name,
              quote: findValueStringBodyStart(args.text, ctx.offset) === undefined,
              tier: 1,
            });
            return tmp;
          }
        : undefined,
  };

  emitResolvedValueCandidates(out, target, {
    engine: ENGINE,
    collectScalarRoutines: () => collectSqliteScalarRoutineDescriptors(),
  });
  // Builtin scalar functions (datetime(), hex(), …) are legal VALUES
  // elements — same builtins-after-policy shape as the scalar-value branch
  // (C3 sol finding 2; sqlite's routine collector is always empty, so
  // without this the typed branch offered no functions at all).
  emitBuiltinFunctions(out);
  return true;
}

/**
 * T19 — INSERT RETURNING list. Emits ONLY the resolved target table's
 * columns (tier 1; sqlite's snapshot carries no routines); returns `false`
 * (generic emission serves the position) when the statement isn't an
 * INSERT/REPLACE, the target can't be resolved, or the cursor is inside a
 * trigger body (RETURNING is illegal there anyway — conservative parity
 * with `emitTypedValuesElement`).
 */
function emitInsertReturningCandidates(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): boolean {
  if (args.triggerHostTable) return false;
  const hit = findInsertReturningTarget(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!hit) return false;
  const tbl = resolveRelation(
    args.snapshotTables,
    foldIdentifier(hit.name),
    hit.schema ? foldIdentifier(hit.schema) : null,
  );
  if (!tbl) return false;
  for (const c of tbl.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${tbl.name})`,
    });
  }
  // This arm short-circuits the generic emission, so the shared projection
  // wildcard has to be added here too — `RETURNING *` is the whole-row form
  // of exactly this list.
  emitProjectionWildcard(out, 'returning', ENGINE);
  emitRowidPseudoColumn(out, tbl);
  return true;
}

/**
 * SQLite's implicit `rowid` pseudo-column. Every ordinary table has it and it
 * is projectable anywhere a real column is (`SELECT rowid`, `RETURNING rowid`),
 * but it is not in `PRAGMA table_info` and so never reaches the schema
 * snapshot — no other emitter can produce it (sqlite script.54 §1/§2).
 *
 * Suppressed for anything that is not an ordinary table (a view has no rowid)
 * and when the table already declares a column of that name — there the
 * explicit declaration is what the schema emission already surfaced.
 * `INTEGER PRIMARY KEY` columns are rowid ALIASES, not replacements, so
 * `rowid` stays legal alongside them and they are not a suppression case.
 *
 * KNOWN LIMITATION: a `WITHOUT ROWID` table has no rowid, but the schema-bus
 * snapshot does not carry that flag (it is not in `PRAGMA table_info`), so the
 * pseudo-column is offered there too. Accepting it yields SQL SQLite rejects —
 * the same class of miss as offering a column of the wrong type; closing it
 * needs a bus-level flag, not a resolver change.
 *
 * SQLite-only by construction: no other engine has a universally projectable
 * implicit row identifier (Oracle's `ROWID` is a pseudo-column too but is
 * emitted from that engine's own vocabulary).
 */
function emitRowidPseudoColumn(out: CompletionCandidate[], tbl: SnapshotTable): void {
  if (tbl.kind !== 'table') return;
  if (tbl.columns.some((c) => foldIdentifier(c.name) === 'rowid')) return;
  out.push({
    label: 'rowid',
    insertText: 'rowid',
    isSnippet: false,
    kind: 'column',
    tier: 2,
    detail: `implicit row id (${tbl.name})`,
  });
}

/** F5.2-A — drop column candidates whose label matches a column already
 *  typed earlier in the same INSERT column-list. No-op outside INSERT
 *  column-list (the helper returns null then). */
function filterAlreadyTypedInsertColumns(
  out: CompletionCandidate[],
  args: BuildArgs,
  ctx: BuildArgs['ctx'],
): void {
  const typed = findTypedInsertColumns(
    args.text,
    ctx.offset,
    ctx.statementRange.start,
    ctx.statementRange.end,
    ENGINE,
  );
  if (!typed || typed.length === 0) return;
  const typedSet = new Set(typed);
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const c = out[i];
    if (c.kind !== 'column') continue;
    if (typedSet.has(c.label.toLowerCase())) {
      out.splice(i, 1);
    }
  }
}

function emitTypeAwareJsonOperators(out: CompletionCandidate[], args: BuildArgs): void {
  const operand = findPrecedingColumnOperand({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    aliasBindings: args.ctx.aliasBindings,
    snapshotTables: args.snapshotTables,
    fold: foldIdentifier,
  });
  if (operand) {
    emitTypeAwareOperators(out, operand.jsonClassification, operand.column.dataType, 'sqlite', args.ctx.positionKind);
    return;
  }
  // Slice 3a — typed expression operand (`CAST(x AS INT) ▮`, `UPPER(c) ▮`).
  const exprType = findPrecedingExpressionType({
    text: args.text,
    offset: args.ctx.offset,
    statementStart: args.ctx.statementRange.start,
    engine: 'sqlite',
  });
  if (exprType) emitTypeAwareExpressionOperators(out, exprType, 'sqlite', args.ctx.positionKind);
}

/**
 * JCS Slice C4 — JSON path completion for SQLite. Mirrors PG's
 * `collectPgJsonPathCandidates`. Detects whether the cursor sits inside a
 * recognised JSON-path string (after `->`, `->>`, `json_extract(col, `),
 * resolves the LHS column reference against the snapshot honouring FROM/JOIN
 * aliases via the shared resolveColumnRefToTable (bindings come from
 * ctx.aliasBindings, a token-stream scan independent of the tree-sitter tree),
 * using SQLite's lowercase-default fold.
 */
function collectSqliteJsonPathCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as SQLite resolves it (by name; `main.` / attached qualifiers). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const pos = findJsonPathStringPosition(args.text, args.offset);
  if (!pos) return [];
  const colSegment = readSqliteIdentifierBefore(args.text, pos.columnSideEnd);
  if (!colSegment) return [];
  const columnRef = colSegment.parts.join('.');
  const resolved = resolveColumnRefToTable(
    columnRef,
    args.aliasBindings,
    args.snapshotTables,
    foldIdentifier,
    args.resolveTable,
  );
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;
  // SQLite snapshot doesn't carry schema names — see the matching note in
  // collectSqliteEnumValueCandidates. MetaInfo writes use "main" by
  // convention, so reads need to use "main" too. ATTACH'd databases get
  // an explicit schema in the snapshot and override this default.
  const hintSchema = tbl.schema || 'main';
  // Offer JSON-path hints when the bus classified the column JSON
  // (engine-native / CHECK) OR the user declared it via MetaInfo
  // ("Treat as JSON" — a plain string column the schema bus can't know
  // about). The declaration source is invalidated by the Table Designer
  // toggle, so this reflects changes without a reconnect.
  const busJson =
    !!colMeta.jsonClassification &&
    colMeta.jsonClassification.kind !== 'not-json';
  if (
    !busJson &&
    !isJsonColumnDeclared(
      args.connectionId, args.database, hintSchema, tbl.name, colMeta.name,
    )
  ) {
    return [];
  }
  const cacheEntry = getPathHints(args.connectionId, args.database, {
    engine: 'sqlite',
    schema: hintSchema,
    table: tbl.name,
    column: colMeta.name,
  });
  const declaredHints = getJsonPathHints(
    args.connectionId,
    args.database,
    hintSchema,
    tbl.name,
    colMeta.name,
  );
  const out: CompletionCandidate[] = [];
  emitJsonPathCandidates(out, {
    cacheEntry,
    declaredHints,
    positionKind: pos.kind,
    partial: pos.partial,
    engine: 'sqlite',
  });
  return out;
}

/**
 * Phase 3 — enum-value completion for SQLite. Resolves the LHS column
 * reference against the snapshot honouring FROM/JOIN aliases via the shared
 * resolveColumnRefToTable (bindings come from ctx.aliasBindings, a
 * token-stream scan independent of the tree-sitter tree), using SQLite's
 * lowercase-default fold.
 *
 * Path-aware extension (MERGE-2): when the LHS of the comparison is a SQLite
 * JSON-extract expression (`col->>'$.key'`, `json_extract(col, '$.key')`),
 * the resolved canonical path is passed to `emitEnumValueCandidates` so the
 * path-keyed MetaInfo enum cache is consulted instead of the column-level
 * cache. Wildcard probe is handled inside `emitEnumValueCandidates`.
 */
// I6 — FTS5 external-content option literals. For `content='<here>'` emit the
// source table names; for `content_rowid='<here>'` emit the columns of the
// sibling content table (PK first). Bare identifiers — the surrounding single
// quotes already exist, so do NOT re-quote via formatIdentifier.
function collectSqliteFtsContentCandidates(args: {
  text: string;
  offset: number;
  statementStart: number;
  snapshotTables: ReadonlyArray<SnapshotTable>;
}): CompletionCandidate[] {
  const slot = findFtsContentLiteralSlot(args.text, args.offset, args.statementStart);
  if (!slot) return [];

  const out: CompletionCandidate[] = [];
  if (slot.option === 'content') {
    for (const t of args.snapshotTables) {
      out.push({
        label: t.name,
        insertText: t.name,
        isSnippet: false,
        kind: 'table',
        tier: 1,
        detail: 'FTS5 content table',
      });
    }
    return out;
  }

  // content_rowid → columns of the resolved content table, PK first.
  if (!slot.contentTable) return out;
  const folded = foldIdentifier(slot.contentTable);
  const tbl = args.snapshotTables.find((t) => foldIdentifier(t.name) === folded);
  if (!tbl) return out;
  for (const c of tbl.columns) {
    out.push({
      label: c.name,
      insertText: c.name,
      isSnippet: false,
      kind: 'column',
      tier: c.isPrimaryKey === true ? 1 : 2,
      detail: `FTS5 content_rowid (${tbl.name})`,
    });
  }
  return out;
}

function collectSqliteEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as SQLite resolves it (by name; `main.` / attached qualifiers). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  // Table DDL carries no queried relation, so a bare column reference here
  // must not fall back to an unrelated snapshot table's cached values
  // (see `isInsideTableDdlStatement`). Closes oracle script.29 §4.
  if (isInsideTableDdlStatement(args.text, args.offset)) return [];
  const cmp = detectComparisonValueContext(args.text, args.offset);
  if (!cmp) {
    // detectComparisonValueContext misses JSON-chain LHS (col->>'$.a' = …)
    // because the char immediately before `=` is the closing quote of the
    // path literal — likewise for `json_extract(col, '$.a') = …` whose LHS
    // ends in `)`. Try the JSON-path extractor directly against each
    // candidate column; the extractor's own findComparisonOpEndBefore gate
    // keeps this cheap — it returns null immediately when the cursor isn't
    // at a comparison RHS.
    return collectSqliteJsonChainEnumValueCandidates(args);
  }

  const resolved = resolveColumnRefToTable(
    cmp.columnRef,
    args.aliasBindings,
    args.snapshotTables,
    foldIdentifier,
    args.resolveTable,
  );
  if (!resolved) return [];
  const tbl = resolved.table;
  const colMeta = resolved.column;

  // Try to extract a JSON path from the cursor context (SQLite-specific).
  // `extractJsonPathFromCursorSqlite` looks at the LHS of the comparison
  // operator for `->>` / `json_extract(col, '…')` expressions and returns
  // the canonical path.
  const pathResult = extractJsonPathFromCursorSqlite({
    text: args.text,
    cursorOffset: args.offset,
    column: colMeta.name,
  });

  const out: CompletionCandidate[] = [];
  emitEnumValueCandidates(out, {
    connectionId: args.connectionId,
    database: args.database,
    // SQLite snapshot generator doesn't populate `tbl.schema` (sqlite has
    // one natural schema, "main"). The MetaInfo store keys cache entries on
    // schema exactly as written — users and autocomplete-coverage specs
    // naturally write "main", so reads need to use "main" too. ATTACH'd dbs
    // get an explicit schema name in the snapshot and override this default.
    schema: tbl.schema || 'main',
    table: tbl.name,
    column: colMeta.name,
    // Pass the canonical path when a JSON-extract expression was detected.
    path: pathResult?.canonicalPath,
    quote: findValueStringBodyStart(args.text, args.offset) === undefined,
  });
  return out;
}

/**
 * Value completion for the SQLite system catalog's closed domains —
 * `WHERE type = '▮'` against `sqlite_master` / `sqlite_schema` /
 * `sqlite_temp_*` offers `table` / `index` / `view` / `trigger`.
 *
 * Separate from `collectSqliteEnumValueCandidates` because the system tables
 * are not in the schema-bus snapshot (introspection filters `sqlite_%`), so
 * `resolveColumnRefToTable` cannot reach them — the relation is recovered
 * from the alias bindings instead, exactly as `emitAliasColumns` recovers
 * their column list. The domain needs no MetaInfo declaration and no
 * sampling: the engine fixes it. Closes sqlite script.64 §3.
 */
function collectSqliteSystemCatalogValueCandidates(args: {
  text: string;
  offset: number;
  readonly aliasBindings: readonly AliasBinding[];
}): CompletionCandidate[] {
  const cmp = detectComparisonValueContext(args.text, args.offset);
  if (!cmp) return [];
  const parts = cmp.columnRef.split('.');
  const columnFolded = foldIdentifier(parts[parts.length - 1] ?? '');
  const qualifier = parts.length > 1 ? foldIdentifier(parts[parts.length - 2]!) : undefined;
  const domain = args.aliasBindings.reduce<readonly string[] | undefined>((found, b) => {
    if (found) return found;
    // A qualified ref (`m.type`) must match the alias that introduced the
    // relation; a bare ref (`type`) may match any relation in scope.
    if (qualifier !== undefined && foldIdentifier(b.alias) !== qualifier) return undefined;
    return getSqliteSystemCatalogValueDomain(foldIdentifier(b.relation), columnFolded);
  }, undefined);
  if (!domain) return [];
  const quote = findValueStringBodyStart(args.text, args.offset) === undefined;
  return domain.map((value, index) => ({
    label: value,
    insertText: quote ? `'${value}'` : value,
    isSnippet: false,
    kind: 'snippet' as const,
    tier: 1 as const,
    detail: 'sqlite_master.type value',
    filterText: value,
    // Declaration order — `table` first, which is what an introspection
    // query filters on most of the time. The ranker would otherwise sort
    // these alphabetically and lead with `index`.
    orderHint: index,
  }));
}

/**
 * Fallback for JSON-chain LHS comparisons that `detectComparisonValueContext`
 * misses (e.g. `col->>'$.key' = '|'`, `json_extract(col, '$.key') = '|'`).
 * Iterates over every column visible via the alias bindings and snapshot
 * tables, runs the SQLite JSON-path extractor for each, and emits enum-value
 * candidates for the first match.
 *
 * The extractor's own `findComparisonOpEndBefore` gate makes this cheap: it
 * returns `null` immediately when the cursor is not at a comparison RHS, so
 * the loop exits fast in non-matching positions.
 */
function collectSqliteJsonChainEnumValueCandidates(args: {
  text: string;
  offset: number;
  connectionId: string;
  database: string;
  snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly aliasBindings: readonly AliasBinding[];
  /** The table a relation reference names, as SQLite resolves it (by name; `main.` / attached qualifiers). */
  readonly resolveTable: RelationResolver<SnapshotTable>;
}): CompletionCandidate[] {
  const quote = findValueStringBodyStart(args.text, args.offset) === undefined;

  // The extractor's backward scan never depends on the column (it is only
  // compared at the end) — scan once, then match the column it names. This
  // fallback runs on nearly every request; one scan per snapshot column was
  // ~20% of provider time on a 600-table schema (1.5.1 F2).
  const pathResult = extractJsonPathFromCursorSqlite({ text: args.text, cursorOffset: args.offset });
  if (!pathResult) return [];
  const targetColumn = pathResult.column.toLowerCase();
  // The statement's own relations first, then the first snapshot table owning the column.
  for (const tbl of boundTablesFirst(args.aliasBindings, args.snapshotTables, args.resolveTable)) {
    for (const col of tbl.columns) {
      if (col.name.toLowerCase() !== targetColumn) continue;

      const out: CompletionCandidate[] = [];
      emitEnumValueCandidates(out, {
        connectionId: args.connectionId,
        database: args.database,
        // Default to "main" when the snapshot doesn't carry a schema name —
        // see the matching note in the non-JSON-chain emitter above.
        schema: tbl.schema || 'main',
        table: tbl.name,
        column: col.name,
        path: pathResult.canonicalPath,
        quote,
      });
      if (out.length > 0) return out;
    }
  }
  return [];
}

/**
 * Read a 1–3-part dotted identifier ending at `offset`. Accepts bare,
 * bracketed `[name]`, double-quoted `"name"`, and backticked `` `name` ``
 * segments — SQLite's four input forms.
 */
function readSqliteIdentifierBefore(
  text: string,
  offset: number,
): { readonly parts: readonly string[] } | null {
  let i = offset;
  while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  const parts: string[] = [];
  while (i > 0) {
    const end = i;
    while (i > 0 && /[A-Za-z0-9_"$`[\]]/.test(text[i - 1]!)) i -= 1;
    if (i === end) break;
    parts.unshift(text.slice(i, end));
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
    if (text[i - 1] !== '.') break;
    i -= 1;
    while (i > 0 && /\s/.test(text[i - 1]!)) i -= 1;
  }
  return parts.length > 0 ? { parts } : null;
}

// Arg-taking introspection pragmas — the call-style `PRAGMA name(arg)` form.
const FUNCTION_STYLE_PRAGMAS = new Set([
  'table_info',
  'table_xinfo',
  'index_info',
  'index_xinfo',
  'index_list',
  'foreign_key_list',
]);

function emitPragmaNames(
  out: CompletionCandidate[],
  opts?: { promoteFunctionStyle?: boolean },
): void {
  for (const name of getSqlitePragmaNames()) {
    const promoted = opts?.promoteFunctionStyle === true && FUNCTION_STYLE_PRAGMAS.has(name);
    // `system-object`, not `keyword`: PRAGMA names are lower-case identifiers
    // in every SQLite document (`PRAGMA foreign_keys = ON`), and the
    // orchestrator's keyword-casing pass upper-cases every `keyword`-kind
    // item — which turned an accepted `foreign_keys` into `FOREIGN_KEYS`.
    out.push({
      label: name,
      insertText: name,
      isSnippet: false,
      kind: 'system-object',
      tier: promoted ? 1 : 2,
      detail: 'PRAGMA',
      filterText: name,
    });
  }
}

function emitVirtualTableModules(out: CompletionCandidate[]): void {
  for (const name of getSqliteVirtualTableModules()) {
    out.push({
      label: name,
      insertText: name,
      isSnippet: false,
      kind: 'keyword',
      tier: 2,
      detail: 'SQLite virtual-table module',
      filterText: name,
    });
  }
}

function emitPragmaValues(out: CompletionCandidate[], pragmaName: string): void {
  const values = getSqlitePragmaValues(pragmaName);
  for (const v of values) {
    out.push({
      label: v,
      insertText: v,
      isSnippet: false,
      kind: 'keyword',
      tier: 2,
      detail: `PRAGMA ${pragmaName}`,
      filterText: v,
    });
  }
}

function emitStrictTypes(out: CompletionCandidate[]): void {
  for (const t of getSqliteStrictTypes()) {
    out.push({
      label: t,
      insertText: t,
      isSnippet: false,
      kind: 'keyword',
      tier: 2,
      detail: 'STRICT type',
      filterText: t,
    });
  }
}

/**
 * Emit the flexible-mode type names (`NUMERIC`, `VARCHAR`, `BOOLEAN`, `DATE`,
 * `DATETIME`) inside a STRICT table at tier 3 — STRICT rejects these at
 * runtime, but they're still legal SQLite tokens; surfacing them at a lower
 * tier lets the user discover that mismatch.
 */
function emitFlexibleTypesDeprioritised(out: CompletionCandidate[]): void {
  const strict = new Set(getSqliteStrictTypes());
  for (const t of getSqliteFlexibleTypes()) {
    if (strict.has(t)) continue;
    out.push({
      label: t,
      insertText: t,
      isSnippet: false,
      kind: 'keyword',
      tier: 3,
      detail: 'SQLite type (flexible — STRICT rejects)',
      filterText: t,
    });
  }
}

/**
 * Emit the full SQLite type-name vocabulary at a non-STRICT data-type slot.
 * STRICT types lead at tier 1 (still the most commonly used affinity names);
 * remaining flexible-mode names follow at tier 2.
 */
function emitDataTypeCandidates(out: CompletionCandidate[]): void {
  const strict = new Set(getSqliteStrictTypes());
  for (const t of getSqliteStrictTypes()) {
    out.push({
      label: t,
      insertText: t,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'SQLite type',
      filterText: t,
    });
  }
  for (const t of getSqliteFlexibleTypes()) {
    if (strict.has(t)) continue;
    out.push({
      label: t,
      insertText: t,
      isSnippet: false,
      kind: 'keyword',
      tier: 2,
      detail: 'SQLite type',
      filterText: t,
    });
  }
}

/**
 * Emit collation names at tier 1. Used by the `… COLLATE |` and
 * `REINDEX <collation>` slots.
 */
function emitCollationNames(out: CompletionCandidate[]): void {
  for (const c of getSqliteCollationNames()) {
    out.push({
      label: c,
      insertText: c,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'SQLite collation',
      filterText: c,
    });
  }
}

/**
 * Emit generated-column storage-class keywords (VIRTUAL, STORED) at tier 1.
 * Used by the post-GENERATED-expression slot.
 */
function emitGeneratedStorageKeywords(out: CompletionCandidate[]): void {
  for (const kw of getSqliteGeneratedStorageKeywords()) {
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'Generated column storage',
      filterText: kw,
    });
  }
}

function emitAttachedDatabases(
  out: CompletionCandidate[],
  attached: readonly AttachedDatabase[],
): void {
  for (const a of attached) {
    out.push({
      label: a.alias,
      insertText: formatIdentifier(a.alias),
      isSnippet: false,
      kind: 'database',
      tier: 4,
      detail: `Attached database (${a.path})`,
      filterText: a.alias,
    });
  }
}

/**
 * SQLite's always-present built-in schemas. `main` is the primary
 * database; `temp` holds temporary objects of the current connection.
 * Both are addressable with a schema-prefix qualifier (`main.Track`,
 * `temp.acact_session_scratch`) so they surface at the FROM-bare slot
 * alongside attached databases.
 */
function emitBuiltinSchemas(out: CompletionCandidate[]): void {
  for (const name of ['main', 'temp'] as const) {
    out.push({
      label: name,
      insertText: name,
      isSnippet: false,
      kind: 'database',
      tier: 4,
      detail: name === 'main' ? 'Built-in primary schema' : 'Built-in temporary schema',
      filterText: name,
      // Fixed 2-name engine built-in set sharing the unbounded 'database'
      // kind — exempt from the ranker's untyped cap (Plan 15 Gate D round
      // 2/3 class kill). Attached databases (user-controlled) stay cappable.
      capExempt: true,
    });
  }
}

function emitDottedAccess(out: CompletionCandidate[], args: BuildArgs): void {
  const { ctx, snapshotTables, cteBindings, onConflictTarget, triggerHostTable, attachedDatabases } = args;
  if (!ctx.dottedBase) return;
  const { parts } = ctx.dottedBase;
  if (parts.length === 0) return;
  const baseFolded = foldIdentifier(parts[0]!);

  if (parts.length === 1) {
    // `excluded.col` inside ON CONFLICT DO UPDATE — surface the target
    // table's columns. Mirrors the PG resolver's `excluded` handling.
    if (baseFolded === 'excluded' && onConflictTarget) {
      emitInsertTargetColumns(out, onConflictTarget, snapshotTables);
      return;
    }
    // F5.2-CB2 — `NEW.col` / `OLD.col` inside a CREATE TRIGGER body resolves
    // to the trigger target-table's columns. The pseudo binding from
    // `pseudoTables.ts` carries `__pseudo:NEW`/`__pseudo:OLD` as the
    // relation; alone it has no column list, so it would fall through to
    // `emitAliasColumns` and produce nothing. Routing to the host table
    // here surfaces the right columns.
    if ((baseFolded === 'new' || baseFolded === 'old') && triggerHostTable) {
      emitTriggerHostColumns(out, triggerHostTable, snapshotTables, baseFolded);
      return;
    }
    // Alias-dot — bound FROM/JOIN alias resolves to its source relation's
    // columns. Anti-SOUNDEX invariant: ONLY this binding's columns surface.
    const aliasMatch = ctx.aliasBindings.find(
      (b) => foldIdentifier(b.alias) === baseFolded,
    );
    if (aliasMatch) {
      emitAliasColumns(out, aliasMatch, snapshotTables, cteBindings, args.text);
      // F5.2-CB5 + S3 — FK/partner-aware column ranking at JOIN ON / WHERE
      // dotted positions (partner affinity → endpoint-pair FKs → name∩+PK).
      applyJoinOnColumnRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'sqlite',
        bindings: ctx.aliasBindings,
        edges: args.snapshotForeignKeys,
        binding: aliasMatch,
        tables: snapshotTables,
      });
      // I2 parity — `json_extract(je.▮` lifts the JSON document column:
      // buffer-affinity (columns this buffer already uses as a JSON1-fn first
      // arg) ∪ bus-classified JSON columns. Buffer-only fallback for the
      // MetaInfo declaration (no connectionId on BuildArgs here).
      if (
        isAtJsonFunctionFirstArg(
          args.text, ctx.offset, ctx.statementRange.start, SQLITE_JSON_FN_NAMES,
        )
      ) {
        applyJsonFunctionArgPromotion(
          out, collectSqliteKnownJsonColumns(args, aliasMatch),
        );
      }
      return;
    }
    // CTE alias-dot — wired here so the CTEs+Subqueries slice's column
    // emission works without resolver edits. The shared `cteScope` already
    // computed `ctx.cteBindings`; we just look up by name.
    const cteMatch = ctx.cteBindings.find(
      (c) => foldIdentifier(c.name) === baseFolded,
    );
    if (cteMatch && !isInOwnNonRecursiveBody(cteMatch, ctx.offset)) {
      emitCteColumnList(out, cteMatch, {
        source: args.text,
        engineId: 'sqlite',
        snapshotTables,
        cteBindings,
      });
      // S3 partner affinity for CTE projections (`ON child.manager_id = ec.▮`
      // wants the FK pair `employee_id`) — name-based, no FK metadata on
      // the CTE itself is needed.
      applyJoinOnColumnRanking({
        out,
        text: args.text,
        offset: ctx.offset,
        statementStart: ctx.statementRange.start,
        sqlEngine: 'sqlite',
        bindings: ctx.aliasBindings,
        edges: args.snapshotForeignKeys,
      });
      return;
    }
    // Attached-db dotted access — `aux.<here>`. Two answers, both wanted:
    // the schema's REAL relations (introspection walks every entry of
    // `PRAGMA database_list`, and in-buffer `CREATE TABLE aux.t` merges in
    // too), followed by the virtual system tables, which exist in EVERY
    // schema and let the user reflect on a freshly attached database that
    // holds nothing yet. Closes coverage sqlite script.38 §5 (concrete
    // tables) and keeps script.64 §2 (empty `:memory:` attachment) answered.
    const attached = attachedDatabases.find(
      (a) => a.alias.toLowerCase() === baseFolded,
    );
    if (attached) {
      emitTablesInDatabase(out, snapshotTables, baseFolded);
      emitSqliteSystemTables(out);
      return;
    }
    // database.<table> — SQLite supports `main.users`, `temp.scratch` and
    // attached-db aliases. Reached for a qualifier the resolver has no
    // attachment record for (`main` / `temp`, or an alias attached outside
    // this connection).
    emitTablesInDatabase(out, snapshotTables, baseFolded);
    return;
  }

  // Two-part `database.table.<col>` — match snapshot tables on (database, name).
  if (parts.length >= 2) {
    const part0Folded = foldIdentifier(parts[0]!);
    const part1Folded = foldIdentifier(parts[1]!);
    const tbl = snapshotTables.find(
      (t) =>
        (t.database ?? '').toLowerCase() === part0Folded &&
        t.name.toLowerCase() === part1Folded,
    );
    if (tbl) {
      for (const c of tbl.columns) {
        out.push({
          label: c.name,
          insertText: formatIdentifier(c.name),
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `${c.dataType} (${tbl.database ?? ''}.${tbl.name})`,
        });
      }
    }
  }
}

function emitTablesInDatabase(
  out: CompletionCandidate[],
  tables: ReadonlyArray<SnapshotTable>,
  databaseFolded: string,
): void {
  for (const t of tables) {
    if ((t.database ?? '').toLowerCase() !== databaseFolded) continue;
    out.push({
      label: formatIdentifier(t.name),
      insertText: formatIdentifier(t.name),
      isSnippet: false,
      kind: t.kind === 'view' ? 'view' : 'table',
      tier: 2,
      detail: t.kind === 'view' ? 'View' : 'Table',
      filterText: t.name,
    });
  }
}

function makeTableCandidate(t: SnapshotTable, fkLinked = false): CompletionCandidate {
  // SQLite is a single-namespace model — no schema-qualified tables in the
  // common case. Output is bare; users who need cross-attached-db
  // qualification (e.g. `aux.users`) write the qualifier explicitly.
  return {
    label: t.name,
    insertText: formatIdentifier(t.name),
    isSnippet: false,
    kind: t.kind === 'view' ? 'view' : 'table',
    // FK-linked tables (an FK edge connects them to a bound relation) are
    // the actually-joinable candidates at a JOIN slot — tier 1 puts them
    // above unrelated tables and above their own `X ON …` FK snippets
    // (shorter-label name tiebreak within the tier).
    tier: fkLinked ? 1 : 2,
    detail: t.kind === 'view' ? 'View' : 'Table',
    filterText: t.name,
  };
}

function emitTables(
  out: CompletionCandidate[],
  tables: ReadonlyArray<SnapshotTable>,
  fkLinkedKeys?: ReadonlySet<string>,
): void {
  for (const t of tables) {
    out.push(makeTableCandidate(t, fkLinkedKeys?.has(fkLinkedTableKey(t)) ?? false));
  }
}

// F5.3 R-bucket: the three SQLite virtual system tables (`sqlite_master`,
// `sqlite_schema`, `sqlite_sequence`) plus their `sqlite_temp_*` mirrors are
// accessible from any SELECT but aren't carried in the schema-bus snapshot
// (introspection filters `sqlite_%`). Surface them at the FROM slot (and at
// `<attached-db>.<here>` dotted access — both schemas expose the same
// virtual relations) so the user can reflect on schema state without
// hand-typing the name. Tier 6 keeps them below user tables in the popup
// ranking while still being discoverable. Closes coverage script.42 §6 +
// script.64 §2.
const SQLITE_SYSTEM_TABLE_NAMES: readonly string[] = [
  'sqlite_master',
  'sqlite_schema',
  'sqlite_sequence',
  'sqlite_temp_master',
  'sqlite_temp_schema',
];

function emitSqliteSystemTables(out: CompletionCandidate[]): void {
  for (const name of SQLITE_SYSTEM_TABLE_NAMES) {
    out.push({
      label: name,
      insertText: formatIdentifier(name),
      isSnippet: false,
      kind: 'table',
      tier: 6,
      detail: 'SQLite system table',
      filterText: name,
      // Engine-shipped curated set (5 names) sharing the unbounded 'table'
      // kind at tier 6 — flag so the ranker's untyped cap can't slice them
      // on a >500-relation schema (Plan 15 Gate D round 2 class kill; the
      // oracle twin was script.24 §2's data-dictionary synonyms).
      capExempt: true,
    });
  }
}

// F5.2-CB8: SQLite (like PG) only allows a CTE to self-reference inside its
// own body when the WITH clause is RECURSIVE. Returns true when the cursor
// sits inside a non-recursive CTE's body, so the resolver must suppress the
// self-binding.
function isInOwnNonRecursiveBody(cte: CteBinding, offset: number): boolean {
  if (cte.recursive) return false;
  if (!cte.bodyRange) return false;
  return offset >= cte.bodyRange.start && offset <= cte.bodyRange.end;
}

function emitCteCandidates(out: CompletionCandidate[], ctx: BuildArgs['ctx']): void {
  for (const cte of ctx.cteBindings) {
    if (isInOwnNonRecursiveBody(cte, ctx.offset)) continue;
    out.push({
      label: cte.name,
      insertText: cte.name,
      isSnippet: false,
      kind: 'cte',
      tier: 5,
      detail: 'CTE',
      filterText: cte.name,
    });
  }
}

/**
 * Emit SQLite built-in function names from the curated list. SQLite has no
 * user-defined routines surfaced through the cache (the adapter's
 * `routines: []` contract), so no dedupe pass is needed. Tier 8
 * (system-function) so built-ins sit at the bottom of the suggestion list —
 * power users still find them via fuzzy match.
 */
function emitBuiltinFunctions(out: CompletionCandidate[]): void {
  for (const fn of getSqliteBuiltinFunctions()) {
    out.push({
      label: fn,
      insertText: `${fn}($0)`,
      isSnippet: true,
      kind: 'system-function',
      tier: 8,
      detail: 'SQLite function',
      filterText: fn,
      triggerSignatureHelp: true,
    });
  }
}

/**
 * F5.2-CB7 — extract the target table name from the `ON <tbl>` segment of a
 * `CREATE INDEX … ON <tbl> (` statement. Returns the bare folded table name
 * (lower-cased, stripped of surrounding quotes/brackets/backticks) or null
 * if the segment isn't recognisable. We only consult the substring before
 * the cursor; the classifier already vouched that the cursor sits inside
 * the open-paren that follows `ON <tbl>`.
 */
function findIndexTargetTable(
  text: string,
  statementStart: number,
  cursorOffset: number,
): string | null {
  const slice = text.slice(statementStart, cursorOffset);
  // Match `ON <ident>` with optional schema qualifier; we only need the
  // final segment of the qualified name (SQLite's snapshot is per-database
  // already). Quoting forms are stripped via `unquoteIdentifier`.
  const m = slice.match(
    /\bON\s+(?:(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\.\s*)?("(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\(/i,
  );
  if (!m || !m[1]) return null;
  const raw = m[1]!;
  return foldIdentifier(unquoteIdentifier(raw));
}

/**
 * GRA8-ROLL-5 — extract the ALTER TABLE target from `ALTER TABLE [<schema>.]<tbl>`
 * in the cursor's statement preamble. Used by the
 * `ddl-alter-table-constraint-column-list` slot (reused for SQLite's
 * `RENAME COLUMN ▮` existing-column slot) to surface the altered table's live
 * columns. SQLite's single-namespace model means only the final identifier is
 * needed; any schema qualifier is dropped and the name is folded (lower-cased,
 * unquoted) to match the snapshot lookup in `emitTargetTableColumns`.
 */
function findSqliteAlterTableTarget(
  text: string,
  statementStart: number,
  cursorOffset: number,
): string | null {
  const slice = text.slice(statementStart, cursorOffset);
  const m = slice.match(
    // Trailing `(?![\w$])` (not `\b`): a closing quote char (`"`, `]`, backtick)
    // is a non-word char, so `\b` never matched after a quoted target followed
    // by whitespace/EOL.
    /\bALTER\s+TABLE\s+(?:(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\.\s*)?("(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)(?![\w$])/i,
  );
  if (!m || !m[1]) return null;
  return foldIdentifier(unquoteIdentifier(m[1]!));
}

/**
 * F5.3 Wave-4 — true when the cursor's innermost unclosed `(` opens a
 * `GENERATED [ALWAYS|BY DEFAULT] AS (` expression. Used to drop the
 * column being currently defined from the in-progress-column emission
 * (referencing self in the GENERATED expression is invalid SQL).
 */
function isInsideGeneratedExpression(text: string, cursorOffset: number): boolean {
  // Walk back over balanced parens to find the innermost unclosed `(`.
  let depth = 0;
  let openIdx = -1;
  for (let i = cursorOffset - 1; i >= 0; i -= 1) {
    const ch = text[i]!;
    if (ch === ')') depth += 1;
    else if (ch === '(') {
      if (depth === 0) {
        openIdx = i;
        break;
      }
      depth -= 1;
    }
  }
  if (openIdx < 0) return false;
  // Inspect the text immediately preceding the `(`.
  const before = text.slice(Math.max(0, openIdx - 64), openIdx);
  return /\bGENERATED\s+(?:ALWAYS|BY\s+DEFAULT)\s+AS\s*$/i.test(before);
}

/**
 * True when the caret's innermost unclosed `(` was opened by a `CHECK` — the
 * one `ddl-create-table-expression` lead whose body is a PREDICATE and so
 * takes operators, not just a comma-separated list of column names.
 */
function isInsideCheckPredicate(
  text: string,
  statementStart: number,
  cursorOffset: number,
): boolean {
  let depth = 0;
  let openIdx = -1;
  for (let i = cursorOffset - 1; i >= statementStart; i -= 1) {
    const ch = text[i]!;
    if (ch === ')') depth += 1;
    else if (ch === '(') {
      if (depth === 0) {
        openIdx = i;
        break;
      }
      depth -= 1;
    }
  }
  if (openIdx < 0) return false;
  return /\bCHECK\s*$/i.test(text.slice(Math.max(statementStart, openIdx - 16), openIdx));
}

/** Comparison + boolean operators that finish a CHECK predicate. Emitted one
 *  tier below the in-progress columns: the column comes first, the operator
 *  after it. */
const SQLITE_CHECK_PREDICATE_OPERATORS: readonly string[] = [
  '=', '<>', '>', '<', '>=', '<=', 'AND', 'OR', 'NOT', 'IN', 'BETWEEN', 'LIKE', 'IS NULL',
  'IS NOT NULL',
];

function emitSqliteCheckPredicateOperators(out: CompletionCandidate[]): void {
  for (const op of SQLITE_CHECK_PREDICATE_OPERATORS) {
    out.push({
      label: op,
      insertText: op,
      isSnippet: false,
      kind: 'keyword',
      tier: 2,
      detail: 'CHECK predicate operator',
    });
  }
}

/**
 * F5.3 Wave-4 — emit declared-so-far columns of an in-progress CREATE TABLE
 * at tier 1. Used by the `ddl-create-table-expression` slot.
 */
function emitInProgressColumns(
  out: CompletionCandidate[],
  columns: readonly string[],
): void {
  for (const col of columns) {
    out.push({
      label: col,
      insertText: formatIdentifier(col),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: 'In-progress column',
    });
  }
}

/**
 * F5.3 Wave-4 — extract the column names declared SO FAR in a CREATE TABLE
 * statement, up to the cursor's position. Walks back from the cursor to the
 * `CREATE TABLE <name> (` opener, then splits the body up to the cursor by
 * top-level commas. The first non-keyword identifier in each segment is the
 * column name; segments that start with a table-constraint keyword
 * (`PRIMARY`, `UNIQUE`, `FOREIGN`, `CONSTRAINT`, `CHECK`) are skipped.
 *
 * Returns the column names in declaration order, deduped (case-insensitive
 * on the raw identifier text — the snapshot's casing wins downstream).
 */
function extractCreateTableInProgressColumns(
  text: string,
  statementStart: number,
  cursorOffset: number,
): readonly string[] {
  const slice = text.slice(statementStart, cursorOffset);
  // Find `CREATE TABLE [TEMP|TEMPORARY] [IF NOT EXISTS] <name> (` in the
  // slice. The opening paren is the column-list opener.
  const headerRe =
    /\bCREATE\s+(?:TEMP(?:ORARY)?\s+)?TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*(?:\s*\.\s*(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*))?)\s*\(/i;
  const m = headerRe.exec(slice);
  if (!m) return [];
  const bodyStart = m.index + m[0].length; // first char inside the column-list `(`
  // The body slice runs from `bodyStart` to the end of `slice` (the cursor).
  // We split by top-level commas — depth tracks parens (the slice ends with
  // an unclosed paren inside the cursor's CHECK / PRIMARY KEY / etc., which
  // contributes a positive depth at the end, so we never close the final
  // segment unless the user already typed it themselves).
  const body = slice.slice(bodyStart);
  const segments: string[] = [];
  let depth = 0;
  let segStart = 0;
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i]!;
    if (ch === '(') depth += 1;
    else if (ch === ')') depth -= 1;
    else if (ch === ',' && depth === 0) {
      segments.push(body.slice(segStart, i));
      segStart = i + 1;
    }
  }
  // Final segment is open (cursor is inside) — include it; the first-ident
  // extraction handles partial segments that consist of just `<name> <type>
  // ... CHECK (`.
  segments.push(body.slice(segStart));

  const FIRST_IDENT_RE = /^\s*(?:"((?:[^"]|"")*)"|\[([^\]]*)\]|`((?:[^`]|``)*)`|([A-Za-z_][\w$]*))/;
  const TABLE_CONSTRAINT_LEAD = new Set([
    'primary',
    'unique',
    'foreign',
    'constraint',
    'check',
  ]);

  const out: string[] = [];
  const seen = new Set<string>();
  for (const seg of segments) {
    const im = FIRST_IDENT_RE.exec(seg);
    if (!im) continue;
    const raw = im[1] ?? im[2] ?? im[3] ?? im[4] ?? '';
    if (!raw) continue;
    // Skip segments that lead with a table-constraint keyword (those are
    // table-level constraints, not column declarations).
    if (TABLE_CONSTRAINT_LEAD.has(raw.toLowerCase())) continue;
    const key = raw.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(raw);
  }
  return out;
}

/**
 * F5.3 Wave-4 — extract the REFERENCED table name from the LAST
 * `REFERENCES <tbl>(` segment before the cursor. SQLite's single-namespace
 * model means we only need the final identifier; any qualifier is stripped.
 * Returns the folded (lower-cased, unquoted) name, or null when no FK
 * segment matches.
 */
function findReferencesTargetTable(
  text: string,
  statementStart: number,
  cursorOffset: number,
): string | null {
  const slice = text.slice(statementStart, cursorOffset);
  const re = /\bREFERENCES\s+(?:(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\.\s*)?("(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s*\(/gi;
  let m: RegExpExecArray | null = null;
  let last: RegExpExecArray | null = null;
  while ((m = re.exec(slice)) !== null) last = m;
  if (!last || !last[1]) return null;
  return foldIdentifier(unquoteIdentifier(last[1]!));
}

/**
 * F5.2-CB7 — emit columns of a target table at tier 1. Used by both the
 * `ddl-index-column` slot and (indirectly) any future slot that needs to
 * surface ONLY one table's columns.
 */
function emitTargetTableColumns(
  out: CompletionCandidate[],
  targetFolded: string,
  tables: ReadonlyArray<SnapshotTable>,
  opts?: { pkFirst?: boolean },
): void {
  const tbl = tables.find((t) => t.name.toLowerCase() === targetFolded);
  if (!tbl) return;
  for (const c of tbl.columns) {
    // pkFirst (REFERENCES column-list): the referenced table's PK is what an
    // FK points at in almost every case — keep it at tier 1 and drop the
    // rest to tier 2 (mirrors the MSSQL resolver).
    const pk = c.isPrimaryKey === true;
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: opts?.pkFirst && !pk ? 2 : 1,
      detail: opts?.pkFirst && pk ? `${c.dataType} (PK, ${tbl.name})` : `${c.dataType} (${tbl.name})`,
    });
  }
}

/**
 * F5.2-CB7 — emit the three DML verbs that are legal after
 * `BEFORE`/`AFTER`/`INSTEAD OF` in a `CREATE TRIGGER` statement. Tier 1 so
 * they outrank anything else (we don't emit anything else in this slot, but
 * the tier matches the user's expectation that there's nothing more
 * relevant).
 */
function emitTriggerEventKeywords(out: CompletionCandidate[]): void {
  for (const kw of ['INSERT', 'UPDATE', 'DELETE']) {
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'Trigger event',
      filterText: kw,
    });
  }
}

// Shared legal-next-keyword model — emit the ordered, grammatically-legal
// continuation keywords for a recognized cursor sub-position, ordered by
// popularity (orderHint = index). Returns true when at least one keyword was
// emitted, so the caller can short-circuit; false (empty set) lets the caller
// fall through to today's full emission.
function emitLegalNextKeywords(
  out: CompletionCandidate[],
  slot: NextKeywordSlotResult,
  // Shifts the popularity order when a leading candidate (the generated
  // table alias) already occupies orderHint 0.
  orderHintBase = 0,
): boolean {
  const kws = getLegalNextKeywords(ENGINE, slot.slot, {
    relationHasAlias: slot.relationHasAlias,
    projectionIsStar: slot.projectionIsStar,
  });
  if (kws.length === 0) return false;
  // The slot IS answered whenever it has a vocabulary, so `true` (the caller's
  // short-circuit) does not depend on how many items are new — a keyword an
  // earlier, more specific emission already offered (a compound remainder at
  // `NULLS F▮`) is skipped rather than repeated in the widget.
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  kws.forEach((kw, i) => {
    if (alreadyOffered.has(kw.toUpperCase())) return;
    out.push({ label: kw, insertText: kw, isSnippet: false, kind: 'keyword', tier: 1, detail: 'SQL keyword', orderHint: orderHintBase + i });
  });
  return true;
}

function emitKeywords(
  out: CompletionCandidate[],
  nextLikely: ReadonlySet<string> = new Set(),
): void {
  const alreadyOffered = keywordLabelsAlreadyOffered(out);
  for (const kw of getSqliteKeywordsForPosition()) {
    // A more specific emission already offered this keyword (e.g. a compound
    // remainder at tier 1) — don't repeat it in the widget.
    if (alreadyOffered.has(kw)) continue;
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: nextLikely.has(kw) ? 1 : 7,
      detail: 'SQL keyword',
    });
  }
}

function emitAllBoundAliasColumns(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: ReadonlyArray<SnapshotTable>,
  cteBindings: readonly CteBinding[],
  text: string,
): void {
  for (const b of bindings) emitAliasColumns(out, b, tables, cteBindings, text);
}

/**
 * Standard SQLite JSON1 table-valued-function projections. Both `json_each`
 * and `json_tree` return rows with this 8-column shape regardless of the JSON
 * input. Surfaced when a FROM-alias binds to one of these TVFs (e.g.
 * `json_each(o.meta) AS je` — `je.|` should list these). Closes
 * goals/autocomplete-coverage sqlite script.12 §1/§2.
 */
const SQLITE_JSON_TVF_COLUMNS: ReadonlyMap<string, readonly string[]> = new Map([
  ['json_each', ['key', 'value', 'type', 'atom', 'id', 'parent', 'fullkey', 'path']],
  ['json_tree', ['key', 'value', 'type', 'atom', 'id', 'parent', 'fullkey', 'path']],
]);

/**
 * I2 parity — the column set treated as JSON at a `json_extract(je.▮` slot:
 * buffer-affinity (columns this buffer already uses as a JSON1-fn first arg)
 * ∪ the binding's bus-classified JSON columns. Lower-cased — the affinity
 * module compares case-insensitively. MetaInfo-declared columns are omitted
 * here (BuildArgs carries no connectionId); the buffer signal covers the
 * common in-query case.
 */
function collectSqliteKnownJsonColumns(
  args: BuildArgs,
  binding: AliasBinding,
): Set<string> {
  const cols = collectBufferJsonFunctionColumns(args.text, SQLITE_JSON_FN_NAMES);
  const dbFolded = binding.database
    ? foldIdentifier(binding.database)
    : binding.schema
      ? foldIdentifier(binding.schema)
      : null;
  const tbl = resolveRelation(args.snapshotTables, foldIdentifier(binding.relation), dbFolded);
  if (!tbl) return cols;
  for (const c of tbl.columns) {
    if (c.jsonClassification && c.jsonClassification.kind !== 'not-json') {
      cols.add(c.name.toLowerCase());
    }
  }
  return cols;
}

function emitAliasColumns(
  out: CompletionCandidate[],
  binding: AliasBinding,
  tables: ReadonlyArray<SnapshotTable>,
  cteBindings: readonly CteBinding[],
  text: string = '',
): void {
  if (binding.kind === 'subquery' || binding.kind === 'derived') {
    // JSON1 TVF aliases — `json_each(...) AS je`, `json_tree(...) AS jt`.
    // The relation parser already classified these as kind='derived' with
    // relation=`json_each`/`json_tree`. There's no SELECT body to infer
    // columns from, so surface the fixed result shape directly.
    const jsonTvfCols = SQLITE_JSON_TVF_COLUMNS.get(binding.relation.toLowerCase());
    if (jsonTvfCols) {
      for (const colName of jsonTvfCols) {
        out.push({
          label: colName,
          insertText: formatIdentifier(colName),
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `${binding.relation} (${binding.alias})`,
        });
      }
      return;
    }
    if (binding.bodyRange && text.length > 0) {
      const inferred = inferSubqueryColumns({
        source: text,
        bodyRange: binding.bodyRange,
        engineId: 'sqlite',
        snapshotTables: tables,
        cteBindings,
      });
      for (const name of inferred) {
        out.push({
          label: name,
          insertText: formatIdentifier(name),
          isSnippet: false,
          kind: 'column',
          tier: 1,
          detail: `(${binding.alias})`,
        });
      }
    }
    return;
  }
  if (binding.kind === 'cte') {
    const relationFolded = foldIdentifier(binding.relation);
    const cte = cteBindings.find((c) => foldIdentifier(c.name) === relationFolded);
    if (!cte) return;
    emitCteColumnList(
      out,
      cte,
      { source: text, engineId: 'sqlite', snapshotTables: tables, cteBindings },
      binding.alias,
    );
    return;
  }
  // Resolve the relation against the snapshot. Database qualifier is
  // optional — SQLite's main DB carries every snapshot table today.
  const relationFolded = foldIdentifier(binding.relation);
  const dbFolded = binding.database
    ? foldIdentifier(binding.database)
    : binding.schema
      ? foldIdentifier(binding.schema)
      : null;
  const tbl = resolveRelation(tables, relationFolded, dbFolded);
  if (tbl) {
    for (const c of tbl.columns) {
      out.push({
        label: c.name,
        insertText: formatIdentifier(c.name),
        isSnippet: false,
        kind: 'column',
        tier: 1,
        detail: `${c.dataType} (${binding.alias})`,
      });
    }
    return;
  }

  // F5.3 Wave-4 — sqlite_master / sqlite_schema / sqlite_temp_*: virtual
  // system tables that aren't carried in the schema-bus snapshot (the
  // introspection query filters `sqlite_%`). Surface their static column
  // list when an alias resolves to one of them. Closes
  // goals/autocomplete-coverage sqlite script.18 §1, §3, §4.
  const sysCols = SQLITE_SYSTEM_TABLE_COLUMNS[relationFolded];
  if (sysCols) {
    for (const colName of sysCols) {
      out.push({
        label: colName,
        insertText: formatIdentifier(colName),
        isSnippet: false,
        kind: 'column',
        tier: 1,
        detail: `TEXT (${binding.alias})`,
      });
    }
  }
}

/**
 * F5.3 Wave-4 — SQLite virtual system tables. These are accessible from any
 * `SELECT` but not part of the user-schema snapshot (introspection filters
 * `sqlite_%`). The schema is fixed and documented at
 * https://www.sqlite.org/schematab.html. `sqlite_schema` is an alias of
 * `sqlite_master`; `sqlite_temp_*` mirror the main pair for the temp DB.
 */
const SQLITE_SYSTEM_TABLE_COLUMNS: Readonly<Record<string, readonly string[]>> = {
  sqlite_master: ['type', 'name', 'tbl_name', 'rootpage', 'sql'],
  sqlite_schema: ['type', 'name', 'tbl_name', 'rootpage', 'sql'],
  sqlite_temp_master: ['type', 'name', 'tbl_name', 'rootpage', 'sql'],
  sqlite_temp_schema: ['type', 'name', 'tbl_name', 'rootpage', 'sql'],
  sqlite_sequence: ['name', 'seq'],
};

/**
 * Resolve a folded relation name to a snapshot table. When the binding
 * explicitly qualifies the database, match `(database, name)`. Otherwise
 * match any name (snapshot only carries one DB's tables).
 */
/** The table a relation reference names, as SQLite resolves it (`resolveRelation`). */
function sqliteTableResolver(tables: ReadonlyArray<SnapshotTable>): RelationResolver<SnapshotTable> {
  return (name, schema) => resolveRelation(tables, foldIdentifier(name), schema ? foldIdentifier(schema) : null);
}

/** The table a bound relation names — FK / loose-FK JOIN identity. */
function sqliteBindingResolver(tables: ReadonlyArray<SnapshotTable>): (binding: AliasBinding) => SnapshotTable | undefined {
  const resolve = sqliteTableResolver(tables);
  return (b) => resolve(b.relation, b.database || b.schema || undefined);
}

function resolveRelation(
  tables: ReadonlyArray<SnapshotTable>,
  relationFolded: string,
  dbFolded: string | null,
): SnapshotTable | undefined {
  if (dbFolded) {
    return tables.find(
      (t) =>
        t.name.toLowerCase() === relationFolded &&
        (t.database ?? '').toLowerCase() === dbFolded,
    );
  }
  return tables.find((t) => t.name.toLowerCase() === relationFolded);
}

/**
 * Surface the target's columns at expression positions inside an
 * `INSERT ... ON CONFLICT DO UPDATE` clause. The `excluded` pseudo-table
 * shadows the target's columns in the SET list, so emitting them at
 * tier 1 keeps unqualified column refs (`name = excluded.name`) responsive.
 */
function emitOnConflictTargetColumns(
  out: CompletionCandidate[],
  target: SqliteInsertTarget,
  tables: ReadonlyArray<SnapshotTable>,
  opts?: { uniqueOnly?: boolean },
): void {
  emitInsertTargetColumns(out, target, tables, target.name, opts);
}

function emitInsertTargetColumns(
  out: CompletionCandidate[],
  target: SqliteInsertTarget,
  tables: ReadonlyArray<SnapshotTable>,
  detailContext?: string,
  opts?: { uniqueOnly?: boolean },
): void {
  const nameFolded = target.name.toLowerCase();
  const dbFolded = target.database ? target.database.toLowerCase() : null;
  const match = dbFolded
    ? tables.find(
        (t) =>
          t.name.toLowerCase() === nameFolded &&
          (t.database ?? '').toLowerCase() === dbFolded,
      )
    : tables.find((t) => t.name.toLowerCase() === nameFolded);
  if (!match) return;
  const ctxLabel = detailContext ?? target.name;
  // uniqueOnly degrades to the full set when the snapshot carries no PK /
  // unique flags at all (stale or partial introspection) — an empty
  // conflict-target list would be strictly worse than an unfiltered one.
  const uniqueCols = opts?.uniqueOnly
    ? match.columns.filter((c) => c.isPrimaryKey || c.isUnique)
    : null;
  const columns = uniqueCols && uniqueCols.length > 0 ? uniqueCols : match.columns;
  for (const c of columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${ctxLabel})`,
    });
  }
}

/**
 * F5.2-CB2 — Emit the trigger host table's columns under the synthetic
 * `NEW` / `OLD` pseudo-relation. The host table is the trigger's
 * `… ON <table>` target; columns come from the snapshot. SQLite folds
 * identifiers to a binary comparable form via `foldIdentifier`, but
 * snapshot column names are emitted as-is.
 */
function emitTriggerHostColumns(
  out: CompletionCandidate[],
  host: TriggerHostTable,
  tables: ReadonlyArray<SnapshotTable>,
  pseudo: 'new' | 'old',
): void {
  const nameLower = host.name.toLowerCase();
  const schemaLower = host.schema ? host.schema.toLowerCase() : undefined;
  const match = tables.find((t) => {
    if (t.name.toLowerCase() !== nameLower) return false;
    if (schemaLower) return (t.schema ?? '').toLowerCase() === schemaLower;
    return true;
  });
  if (!match) return;
  const pseudoLabel = pseudo === 'new' ? 'NEW' : 'OLD';
  for (const c of match.columns) {
    out.push({
      label: c.name,
      insertText: formatIdentifier(c.name),
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `${c.dataType} (${pseudoLabel} → ${match.name})`,
    });
  }
}

type CteResolveArgs = {
  readonly source: string;
  readonly engineId: DatabaseEngineId;
  readonly snapshotTables: ReadonlyArray<SnapshotTable>;
  readonly cteBindings: readonly CteBinding[];
};

function emitCteColumnList(
  out: CompletionCandidate[],
  cte: CteBinding,
  resolveArgs: CteResolveArgs,
  aliasOrCteName?: string,
): void {
  const cols = resolveCteColumns({ cte, ...resolveArgs });
  if (cols.length === 0) return;
  const detailContext = aliasOrCteName ?? cte.name;
  for (const col of cols) {
    out.push({
      label: col,
      insertText: col,
      isSnippet: false,
      kind: 'column',
      tier: 1,
      detail: `Column (${detailContext})`,
    });
  }
}

/**
 * Emit FK-driven JOIN snippets when the cursor is in JOIN position with at
 * least one alias bound. For every (binding × snapshotTable) pair, look up
 * the applicable FK edges (forward + reverse) and emit one snippet per edge.
 * Mirrors the MSSQL / PG / MySQL resolver shape — column lookup is inline
 * because SQLite's bus snapshot already carries columns per table.
 *
 * The ranker auto-promotes `fkJoin: true` candidates to tier 1 in `from`
 * position with bound aliases; outside that they fall to tier 6 so a
 * misplaced snippet cannot outrank real columns or relations.
 */
function emitFkJoinSnippets(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  tables: ReadonlyArray<SnapshotTable>,
  fks: readonly FkEdge[],
): void {
  const existingAliases = new Set<string>(bindings.map((b) => b.alias.toLowerCase()));
  const seen = new Set<string>();
  // Relation identity (`fkEndpointMatch.ts`), the rule every dialect shares.
  const match = makeFkEndpointMatcher(tables, sqliteBindingResolver(tables));
  // One index per pass — never cached across requests (`fkEdgeIndex.ts`).
  const applicableFks = makeApplicableFksFinder(fks, match);
  for (const binding of bindings) {
    if (binding.kind === 'cte' || binding.kind === 'subquery' || binding.kind === 'derived') {
      continue;
    }
    for (const t of tables) {
      const candidate: TableMeta = {
        name: t.name,
        schema: t.schema,
        database: t.database,
        kind: (t.kind === 'view' ? 'view' : 'table') as TableMeta['kind'],
        columns: [],
      };
      const applicable = applicableFks(binding, candidate);
      for (const fk of applicable) {
        const dedupeKey = `${binding.alias.toLowerCase()}|${t.database ?? ''}.${t.name}|${fk.name ?? `${fk.fromTable}->${fk.toTable}`}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        out.push(
          generateFkJoinSnippet({
            from: binding,
            candidate,
            fk,
            existingAliases,
            engine: ENGINE,
            match,
          }),
        );
      }
    }
  }
}

// Test-only exports so unit tests can exercise the schema-aware emission +
// identifier helpers without standing up the full Monaco translation.
export function __testEmitTables(
  tables: ReadonlyArray<SnapshotTable>,
): CompletionCandidate[] {
  const out: CompletionCandidate[] = [];
  emitTables(out, tables);
  return out;
}

export function __testNeedsDoubleQuote(s: string): boolean {
  return needsDoubleQuote(s);
}

export function __testDoubleQuote(s: string): string {
  return doubleQuote(s);
}

export function __testUnquote(s: string): string {
  return unquoteIdentifier(s);
}

export function __testFormatQualifiedTwoPart(database: string, name: string): string {
  return formatQualifiedTwoPart(database, name);
}


